import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { WordPressPostsField } from "./WordPressPostsField.js";
import {
  Activity,
  ArrowDown,
  ArrowRight,
  ArrowUp,
  Check,
  CircleAlert,
  Clock3,
  CloudUpload,
  Copy,
  Download,
  FileImage,
  FileStack,
  GalleryVerticalEnd,
  GripVertical,
  MonitorCheck,
  MonitorPlay,
  MoreHorizontal,
  Minus,
  Music2,
  Plus,
  Pencil,
  RefreshCw,
  Search,
  Settings,
  ShieldCheck,
  Gamepad2,
  Shuffle,
  Sparkles,
  Tv,
  Trash2,
  Upload,
  Users,
  VolumeX,
  WandSparkles,
  X,
} from "lucide-react";
import { useEffect, useRef, useState, type CSSProperties, type ReactNode } from "react";
import { useForm } from "react-hook-form";
import { useNavigate, useSearchParams } from "react-router-dom";
import {
  slideTypeHasVideo,
  supportedFontFamilies,
  type FieldDefinition,
  type SlideFieldValues,
  type SlideTypeDefinition,
} from "@qassist/schemas";
import { builtInSlideTypes } from "@qassist/schemas/seeds";
import {
  api,
  loadSession,
  saveSession,
  uploadAssetWithProgress,
  type AccessMode,
  type Session,
} from "./api.js";
import { TemplateGuiBuilder } from "./TemplateGuiBuilder.js";
import { ColorPicker } from "./ColorPicker.js";
import { ScaledSlideRenderer } from "./ScaledSlideRenderer.js";
import { ScreenPreview } from "./ScreenPreview.js";
import { stepTvZoom, TV_ZOOM_MAX, TV_ZOOM_MIN, TV_ZOOM_STEP } from "./playerZoom.js";
import { useModalFocus } from "./useModalFocus.js";

const timezoneOptions = [
  ["Asia/Qatar", "Doha (Asia/Qatar)"],
  ["Asia/Riyadh", "Riyadh (Asia/Riyadh)"],
  ["Asia/Dubai", "Dubai (Asia/Dubai)"],
  ["Asia/Kuwait", "Kuwait (Asia/Kuwait)"],
  ["Asia/Bahrain", "Bahrain (Asia/Bahrain)"],
  ["Asia/Muscat", "Muscat (Asia/Muscat)"],
  ["Asia/Baghdad", "Baghdad (Asia/Baghdad)"],
  ["Asia/Amman", "Amman (Asia/Amman)"],
  ["Asia/Beirut", "Beirut (Asia/Beirut)"],
  ["Africa/Cairo", "Cairo (Africa/Cairo)"],
  ["Europe/London", "London (Europe/London)"],
  ["UTC", "UTC"],
] as const;
const slideFontOptions = [...supportedFontFamilies];
const androidTvApkUrl = "/downloads/android-tv/qassist-tv.apk";
const androidTvApkMetadataUrl = "/downloads/android-tv/metadata.json";

interface AndroidTvApkMetadata {
  versionName: string;
  versionCode: number;
  sizeBytes: number;
  sha256: string;
  publishedAt: string;
}

function formatApkSize(sizeBytes: number): string {
  return `${new Intl.NumberFormat(undefined, { maximumFractionDigits: 1 }).format(
    sizeBytes / 1_000_000,
  )} MB`;
}

const entranceAnimationOptions = [
  "none",
  "fade-in",
  "blur-in",
  "slide-in-left",
  "slide-in-right",
  "slide-in-up",
  "slide-in-down",
  "zoom-in-subtle",
  "zoom-in",
  "bounce-in",
  "rotate-in",
  "flip-in",
  "reveal-up",
] as const;
const exitAnimationOptions = [
  "none",
  "fade-out",
  "slide-out-left",
  "slide-out-right",
  "slide-out-up",
  "slide-out-down",
  "zoom-out-subtle",
] as const;

function portableFilename(value: string): string {
  const safe = value
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");
  return safe || "qassist-export";
}

function downloadJsonFile(value: unknown, filename: string): void {
  const url = URL.createObjectURL(
    new Blob([`${JSON.stringify(value, null, 2)}\n`], { type: "application/json" }),
  );
  const link = document.createElement("a");
  link.href = url;
  link.download = filename;
  link.click();
  URL.revokeObjectURL(url);
}

async function readJsonFile(file: File): Promise<unknown> {
  if (file.size > 10 * 1024 * 1024)
    throw new Error("The import file is larger than the supported 10 MB limit.");
  try {
    return JSON.parse(await file.text()) as unknown;
  } catch {
    throw new Error("Choose a valid Q-Assist TV JSON export file.");
  }
}
const alignmentOptions = ["left", "center", "right"];
const positionOptions = [
  "top-left",
  "top-center",
  "top-right",
  "center",
  "bottom-left",
  "bottom-center",
  "bottom-right",
];

function TimezoneSelect({
  value,
  onChange,
  required = false,
}: {
  value: string;
  onChange(value: string): void;
  required?: boolean;
}) {
  const known = timezoneOptions.some(([timezone]) => timezone === value);
  return (
    <select value={value} onChange={(event) => onChange(event.target.value)} required={required}>
      {!known && value ? <option value={value}>{value}</option> : null}
      {timezoneOptions.map(([timezone, label]) => (
        <option value={timezone} key={timezone}>
          {label}
        </option>
      ))}
    </select>
  );
}

function ErrorNotice({ error }: { error: unknown }) {
  if (!error) return null;
  return (
    <div className="notice notice--error" role="alert">
      <CircleAlert size={18} />
      <div>
        <strong>Something needs attention</strong>
        <span>
          {error instanceof Error ? error.message : "The request could not be completed."}
        </span>
      </div>
    </div>
  );
}

function PageLoading() {
  return (
    <div className="page-loading" role="status">
      <span className="spinner" />
      <span>Loading workspace data…</span>
    </div>
  );
}

function Modal({
  title,
  description,
  children,
  onClose,
  size = "default",
}: {
  title: string;
  description?: string;
  children: ReactNode;
  onClose(): void;
  size?: "default" | "large" | "workspace" | "fullscreen";
}) {
  const dialogRef = useModalFocus(true, onClose);

  return (
    <div
      className={`modal-layer ${size === "fullscreen" ? "modal-layer--fullscreen" : ""}`}
      role="presentation"
    >
      <button className="modal-scrim" onClick={onClose} aria-label="Close dialog" tabIndex={-1} />
      <section
        ref={dialogRef}
        className={`modal ${
          size === "workspace"
            ? "modal--workspace"
            : size === "fullscreen"
              ? "modal--fullscreen"
              : size === "large"
                ? "modal--large"
                : ""
        }`}
        role="dialog"
        aria-modal="true"
        aria-labelledby="modal-title"
        tabIndex={-1}
      >
        <header className="modal-head">
          <div>
            <h2 id="modal-title">{title}</h2>
            {description ? <p>{description}</p> : null}
          </div>
          <button
            className="icon-button"
            onClick={onClose}
            aria-label="Close dialog"
            data-modal-initial-focus
          >
            <X size={20} />
          </button>
        </header>
        <div className="modal-body">{children}</div>
      </section>
    </div>
  );
}

interface OverviewAdmin {
  clients: number;
  tvs: number;
  slides: number;
  jobs: Array<{ id: string; jobName: string; outcome: string; startedAt: string }>;
}

interface OverviewClient {
  client: {
    name: string;
    effectiveStatus: string;
    activeUntil?: string;
  };
  counts: { slides: number; playlists: number; tvs: number };
  recentTvs: Array<{ id: string; name: string; lastSeenAt?: string; status: string }>;
}

export function OverviewPage({ mode }: { mode: AccessMode }) {
  const navigate = useNavigate();
  const query = useQuery({
    queryKey: [mode, "overview"],
    queryFn: () =>
      api<OverviewAdmin | OverviewClient>(
        mode === "admin" ? "/api/v1/admin/overview" : "/api/v1/client/overview",
      ),
  });
  const apkMetadataQuery = useQuery({
    queryKey: ["android-tv-apk-metadata"],
    enabled: mode === "admin",
    retry: false,
    queryFn: async () => {
      const response = await fetch(androidTvApkMetadataUrl, { cache: "no-store" });
      if (!response.ok) throw new Error("The Android TV APK has not been published.");
      return (await response.json()) as AndroidTvApkMetadata;
    },
  });
  if (query.isLoading) return <PageLoading />;
  if (query.error) return <ErrorNotice error={query.error} />;
  if (!query.data) return null;

  const admin = mode === "admin" ? (query.data as OverviewAdmin) : null;
  const client = mode === "client" ? (query.data as OverviewClient) : null;
  const stats = admin
    ? [
        {
          label: "Active clients",
          value: admin.clients,
          note: "Across the platform",
          icon: Users,
          tone: "teal",
        },
        {
          label: "Managed displays",
          value: admin.tvs,
          note: "All tenant devices",
          icon: Tv,
          tone: "blue",
        },
        {
          label: "Published content",
          value: admin.slides,
          note: "Slides and announcements",
          icon: GalleryVerticalEnd,
          tone: "amber",
        },
        {
          label: "Platform status",
          value: "Healthy",
          note: "API and workers ready",
          icon: Activity,
          tone: "status",
        },
      ]
    : [
        {
          label: "Slides",
          value: client?.counts.slides ?? 0,
          note: "Ready for playlists",
          icon: GalleryVerticalEnd,
          tone: "teal",
        },
        {
          label: "Playlists",
          value: client?.counts.playlists ?? 0,
          note: "Content sequences",
          icon: FileStack,
          tone: "blue",
        },
        {
          label: "Televisions",
          value: client?.counts.tvs ?? 0,
          note: "Configured displays",
          icon: Tv,
          tone: "amber",
        },
        {
          label: "Account",
          value: client?.client.effectiveStatus ?? "—",
          note: "Subscription state",
          icon: ShieldCheck,
          tone: "status",
        },
      ];

  return (
    <div className="page-stack">
      <section className="welcome-row">
        <div>
          <span className="eyebrow">
            <Sparkles size={14} /> Live workspace
          </span>
          <h2>
            {mode === "admin"
              ? "The platform at a glance"
              : `Good to see you, ${client?.client.name ?? "there"}`}
          </h2>
          <p>Monitor what is live, prepare the next update, and keep every screen dependable.</p>
        </div>
        <button
          className="button button--primary"
          onClick={() => navigate(mode === "admin" ? "/admin/clients" : "/client/content")}
        >
          <Plus size={17} /> {mode === "admin" ? "Manage clients" : "Manage content"}
        </button>
      </section>
      <section className="stats-grid">
        {stats.map((stat) => {
          const Icon = stat.icon;
          return (
            <article className="stat-card" key={stat.label}>
              <span className={`stat-icon stat-icon--${stat.tone}`}>
                <Icon size={21} />
              </span>
              <span>{stat.label}</span>
              <strong>{stat.value}</strong>
              <small>{stat.note}</small>
            </article>
          );
        })}
      </section>
      <section className="overview-grid">
        <article className="card activity-card">
          <header className="card-head">
            <div>
              <h3>Operational pulse</h3>
              <p>Recent activity and system signals</p>
            </div>
          </header>
          <div className="activity-list">
            {(admin?.jobs.length
              ? admin.jobs.map((job) => ({
                  id: job.id,
                  title: job.jobName,
                  text: `Background job ${job.outcome}`,
                  time: new Date(job.startedAt).toLocaleString(),
                }))
              : [
                  {
                    id: "content",
                    title: "Content service",
                    text: "Ready for the next publish",
                    time: "Live",
                  },
                  {
                    id: "cache",
                    title: "Player cache",
                    text: "Atomic refresh protection enabled",
                    time: "Live",
                  },
                  {
                    id: "ticker",
                    title: "Ticker worker",
                    text: "Six-hour refresh schedule configured",
                    time: "Scheduled",
                  },
                ]
            ).map((item) => (
              <div className="activity-item" key={item.id}>
                <span className="activity-dot">
                  <Check size={13} />
                </span>
                <div>
                  <strong>{item.title}</strong>
                  <span>{item.text}</span>
                </div>
                <time>{item.time}</time>
              </div>
            ))}
          </div>
        </article>
        <article className="card quick-card">
          <header className="card-head">
            <div>
              <h3>Quick actions</h3>
              <p>Common workspace tasks</p>
            </div>
          </header>
          <div className="quick-actions">
            {(mode === "admin"
              ? [
                  { label: "Manage clients", icon: Users, path: "/admin/clients" },
                  {
                    label: "Manage slide types",
                    icon: WandSparkles,
                    path: "/admin/slide-types",
                  },
                  { label: "Review TV health", icon: MonitorCheck, path: "/admin/tvs" },
                ]
              : [
                  { label: "Manage content", icon: FileStack, path: "/client/content" },
                  { label: "Manage televisions", icon: MonitorPlay, path: "/client/tvs" },
                ]
            ).map((action) => {
              const Icon = action.icon;
              return (
                <button key={action.label} onClick={() => navigate(action.path)}>
                  <span>
                    <Icon size={18} />
                  </span>
                  {action.label}
                  <ArrowRight size={16} />
                </button>
              );
            })}
            {mode === "admin" && apkMetadataQuery.data ? (
              <a href={androidTvApkUrl} aria-label="Download Android TV APK">
                <span>
                  <Download size={18} />
                </span>
                <span className="quick-action-copy">
                  <strong>Download Android TV APK</strong>
                  <small>
                    Version {apkMetadataQuery.data.versionName} ·{" "}
                    {formatApkSize(apkMetadataQuery.data.sizeBytes)}
                  </small>
                </span>
                <Download size={16} />
              </a>
            ) : mode === "admin" ? (
              <div className="quick-action quick-action--disabled" aria-disabled="true">
                <span>
                  <Download size={18} />
                </span>
                <span className="quick-action-copy">
                  <strong>Android TV APK unavailable</strong>
                  <small>
                    {apkMetadataQuery.isLoading
                      ? "Checking locally hosted release…"
                      : "Run an Android release build to publish it."}
                  </small>
                </span>
              </div>
            ) : null}
          </div>
        </article>
      </section>
    </div>
  );
}

interface ClientRow {
  id: string;
  name: string;
  slug: string;
  status: string;
  effectiveStatus: string;
  activeUntil?: string;
  email: string;
  brandName?: string | null;
  brandLogoUrl?: string | null;
  brandPrimaryColor: string;
  brandSecondaryColor: string;
  createdAt: string;
}

export function AdminClientsPage() {
  const queryClient = useQueryClient();
  const [creating, setCreating] = useState(false);
  const [selectedClient, setSelectedClient] = useState<ClientRow | null>(null);
  const [search, setSearch] = useState("");
  const [formError, setFormError] = useState("");
  const [subscriptionUntil, setSubscriptionUntil] = useState("2030-12-31");
  const [brandingForm, setBrandingForm] = useState({
    brandName: "",
    brandPrimaryColor: "#087b82",
    brandSecondaryColor: "#06656b",
  });
  const [accountForm, setAccountForm] = useState({ email: "", newPassword: "" });
  const query = useQuery({
    queryKey: ["admin", "clients"],
    queryFn: () => api<ClientRow[]>("/api/v1/admin/clients"),
  });
  const { register, handleSubmit, reset, formState } = useForm<{
    name: string;
    slug: string;
    email: string;
    password: string;
    activeUntil: string;
  }>({
    defaultValues: { name: "", slug: "", email: "", password: "", activeUntil: "2030-12-31" },
  });
  const create = useMutation({
    mutationFn: (input: {
      name: string;
      slug: string;
      email: string;
      password: string;
      activeUntil: string;
    }) =>
      api<ClientRow>("/api/v1/admin/clients", {
        method: "POST",
        body: JSON.stringify({
          ...input,
          activeUntil: new Date(`${input.activeUntil}T23:59:59Z`).toISOString(),
          expiredTvPolicy: "continue_cached",
        }),
      }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ["admin", "clients"] });
      reset();
      setCreating(false);
    },
    onError: (error) =>
      setFormError(error instanceof Error ? error.message : "Client could not be created."),
  });
  const updateClient = useMutation({
    mutationFn: (input: Record<string, unknown>) =>
      api<ClientRow>(`/api/v1/admin/clients/${selectedClient?.id ?? ""}`, {
        method: "PATCH",
        body: JSON.stringify(input),
      }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ["admin", "clients"] });
      setSelectedClient(null);
    },
  });
  const reactivateClient = useMutation({
    mutationFn: () =>
      api<ClientRow>(`/api/v1/admin/clients/${selectedClient?.id ?? ""}/reactivate`, {
        method: "POST",
        body: JSON.stringify({
          activeUntil: new Date(`${subscriptionUntil}T23:59:59Z`).toISOString(),
          note: "Reactivated from the platform client manager",
        }),
      }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ["admin", "clients"] });
      setSelectedClient(null);
    },
  });
  const impersonateClient = useMutation({
    mutationFn: () =>
      api<Session>(`/api/v1/admin/clients/${selectedClient?.id ?? ""}/impersonate`, {
        method: "POST",
      }),
    onSuccess: (clientSession) => {
      const adminSession = loadSession();
      if (!adminSession) return;
      saveSession({ ...clientSession, returnSession: adminSession });
      window.location.assign("/client");
    },
  });
  function manageClient(client: ClientRow): void {
    setSelectedClient(client);
    setBrandingForm({
      brandName: client.brandName ?? client.name,
      brandPrimaryColor: client.brandPrimaryColor,
      brandSecondaryColor: client.brandSecondaryColor,
    });
    setAccountForm({ email: client.email, newPassword: "" });
    const currentEnd = client.activeUntil ? new Date(client.activeUntil) : null;
    const suggested = new Date();
    suggested.setFullYear(suggested.getFullYear() + 1);
    setSubscriptionUntil(
      (currentEnd && currentEnd > new Date() ? currentEnd : suggested).toISOString().slice(0, 10),
    );
  }
  const rows =
    query.data?.filter(
      (client) =>
        client.name.toLowerCase().includes(search.toLowerCase()) ||
        client.slug.includes(search.toLowerCase()),
    ) ?? [];

  return (
    <div className="page-stack">
      <section className="page-actions">
        <div>
          <h2>Client accounts</h2>
          <p>Control tenant access, subscription windows, and player branding.</p>
        </div>
        <button
          className="button button--primary"
          onClick={() => {
            setFormError("");
            setCreating(true);
          }}
        >
          <Plus size={17} /> New client
        </button>
      </section>
      <section className="card table-card">
        <div className="table-tools">
          <label className="search-field">
            <Search size={17} />
            <input
              placeholder="Search clients…"
              value={search}
              onChange={(event) => setSearch(event.target.value)}
            />
          </label>
          <span className="muted">{rows.length} clients</span>
        </div>
        {query.isLoading ? (
          <PageLoading />
        ) : query.error ? (
          <ErrorNotice error={query.error} />
        ) : (
          <div className="table-scroll">
            <table>
              <thead>
                <tr>
                  <th>Client</th>
                  <th>Status</th>
                  <th>Subscription ends</th>
                  <th>Created</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {rows.map((client) => (
                  <tr key={client.id}>
                    <td>
                      <div className="entity-cell">
                        <span className="entity-avatar">
                          {client.name.slice(0, 2).toUpperCase()}
                        </span>
                        <div>
                          <strong>{client.name}</strong>
                          <small>/{client.slug}</small>
                        </div>
                      </div>
                    </td>
                    <td>
                      <span
                        className={`status-pill status-pill--${client.effectiveStatus === "active" ? "good" : "warn"}`}
                      >
                        {client.effectiveStatus}
                      </span>
                    </td>
                    <td>
                      {client.activeUntil
                        ? new Date(client.activeUntil).toLocaleDateString()
                        : "No expiry"}
                    </td>
                    <td>{new Date(client.createdAt).toLocaleDateString()}</td>
                    <td>
                      <button
                        className="icon-button"
                        aria-label={`Actions for ${client.name}`}
                        onClick={() => manageClient(client)}
                      >
                        <MoreHorizontal size={18} />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>
      {creating ? (
        <Modal
          title="Create a client"
          description="Create the tenant and its first client login together. The tenant starts active and uses safe cached playback if its subscription expires."
          onClose={() => {
            setCreating(false);
            setFormError("");
            reset();
          }}
        >
          <form
            className="form-stack"
            onSubmit={(event) => void handleSubmit((values) => create.mutate(values))(event)}
          >
            <label>
              <span>Business name</span>
              <input
                {...register("name", { required: true, minLength: 2 })}
                placeholder="Qatar Welcome Center"
              />
            </label>
            <label>
              <span>Workspace slug</span>
              <input
                {...register("slug", { required: true, pattern: /^[a-z0-9]+(?:-[a-z0-9]+)*$/ })}
                placeholder="qatar-welcome"
              />
            </label>
            <label>
              <span>Client email</span>
              <input
                type="email"
                autoComplete="email"
                {...register("email", { required: true })}
                placeholder="manager@example.com"
              />
            </label>
            <label>
              <span>Client password</span>
              <input
                type="password"
                autoComplete="new-password"
                {...register("password", {
                  required: true,
                  minLength: 12,
                  pattern: /^(?=.*[a-z])(?=.*[A-Z])(?=.*[0-9])(?=.*[^A-Za-z0-9]).+$/,
                })}
                placeholder="At least 12 characters"
              />
              <small className="field-help">
                Include uppercase, lowercase, number, and symbol characters.
              </small>
            </label>
            <label>
              <span>Subscription ends</span>
              <input type="date" {...register("activeUntil", { required: true })} />
            </label>
            {formError || formState.errors.slug || formState.errors.password ? (
              <div className="form-error">
                {formError ||
                  (formState.errors.slug
                    ? "Use lowercase letters, numbers, and hyphens for the slug."
                    : "Use at least 12 characters with uppercase, lowercase, number, and symbol characters.")}
              </div>
            ) : null}
            <div className="modal-actions">
              <button
                type="button"
                className="button button--secondary"
                onClick={() => {
                  setCreating(false);
                  setFormError("");
                  reset();
                }}
              >
                Cancel
              </button>
              <button type="submit" className="button button--primary" disabled={create.isPending}>
                {create.isPending ? "Creating…" : "Create client"}
              </button>
            </div>
          </form>
        </Modal>
      ) : null}
      {selectedClient ? (
        <Modal
          title={`Manage ${selectedClient.name}`}
          description="Control access, dashboard identity, and player branding for this customer."
          onClose={() => setSelectedClient(null)}
        >
          <div className="form-stack">
            <div className="notice">
              <ShieldCheck size={18} />
              <span>
                Current effective status: <strong>{selectedClient.effectiveStatus}</strong>
              </span>
            </div>
            <div className="form-grid">
              <label>
                <span>Brand name</span>
                <input
                  value={brandingForm.brandName}
                  onChange={(event) =>
                    setBrandingForm({ ...brandingForm, brandName: event.target.value })
                  }
                  required
                />
              </label>
              <label>
                <span>Player primary color</span>
                <ColorPicker
                  value={brandingForm.brandPrimaryColor}
                  onChange={(value) =>
                    setBrandingForm({ ...brandingForm, brandPrimaryColor: value })
                  }
                />
              </label>
              <label>
                <span>Player secondary color</span>
                <ColorPicker
                  value={brandingForm.brandSecondaryColor}
                  onChange={(value) =>
                    setBrandingForm({ ...brandingForm, brandSecondaryColor: value })
                  }
                />
              </label>
            </div>
            <fieldset>
              <legend>Client login</legend>
              <div className="form-grid">
                <label>
                  <span>Email</span>
                  <input
                    type="email"
                    autoComplete="email"
                    value={accountForm.email}
                    onChange={(event) =>
                      setAccountForm({ ...accountForm, email: event.target.value })
                    }
                    required
                  />
                </label>
                <label>
                  <span>Reset password</span>
                  <input
                    type="password"
                    autoComplete="new-password"
                    minLength={12}
                    value={accountForm.newPassword}
                    onChange={(event) =>
                      setAccountForm({ ...accountForm, newPassword: event.target.value })
                    }
                    placeholder="Leave blank to keep current password"
                  />
                </label>
              </div>
              <small className="field-help">
                Saving an email or password change signs this client account out on other devices.
              </small>
            </fieldset>
            <label>
              <span>Subscription end date</span>
              <input
                type="date"
                min={new Date().toISOString().slice(0, 10)}
                value={subscriptionUntil}
                onChange={(event) => setSubscriptionUntil(event.target.value)}
                required
              />
            </label>
            <div
              className="brand-preview"
              style={
                {
                  "--brand-primary": brandingForm.brandPrimaryColor,
                  "--brand-secondary": brandingForm.brandSecondaryColor,
                } as CSSProperties
              }
            >
              <MonitorPlay size={22} />
              <strong>{brandingForm.brandName || selectedClient.name}</strong>
            </div>
            <ErrorNotice error={updateClient.error ?? reactivateClient.error} />
            <ErrorNotice error={impersonateClient.error} />
            <div className="modal-actions">
              <button
                type="button"
                className="button button--secondary"
                disabled={impersonateClient.isPending}
                onClick={() => impersonateClient.mutate()}
              >
                {impersonateClient.isPending ? "Opening…" : "Login as client"}
              </button>
              <button
                type="button"
                className="button button--secondary"
                onClick={() => setSelectedClient(null)}
              >
                Cancel
              </button>
              <button
                type="button"
                className="button button--secondary"
                disabled={updateClient.isPending || selectedClient.status === "suspended"}
                onClick={() => updateClient.mutate({ status: "suspended" })}
              >
                Suspend client
              </button>
              {selectedClient.effectiveStatus === "expired" ? (
                <button
                  type="button"
                  className="button button--primary"
                  disabled={reactivateClient.isPending || !subscriptionUntil}
                  onClick={() => reactivateClient.mutate()}
                >
                  {reactivateClient.isPending ? "Reactivating…" : "Reactivate client"}
                </button>
              ) : (
                <button
                  type="button"
                  className="button button--secondary"
                  disabled={updateClient.isPending || selectedClient.status === "active"}
                  onClick={() => updateClient.mutate({ status: "active" })}
                >
                  Activate client
                </button>
              )}
              <button
                type="button"
                className="button button--primary"
                disabled={
                  updateClient.isPending || !brandingForm.brandName || !accountForm.email.trim()
                }
                onClick={() =>
                  updateClient.mutate({
                    ...brandingForm,
                    email: accountForm.email.trim(),
                    ...(accountForm.newPassword ? { newPassword: accountForm.newPassword } : {}),
                  })
                }
              >
                Save client
              </button>
            </div>
          </div>
        </Modal>
      ) : null}
    </div>
  );
}

interface SlideTypeRow {
  id: string;
  versionId: string;
  key: string;
  name: string;
  description: string;
  status?: string;
  latestVersion?: number;
  definition: SlideTypeDefinition;
}

function sampleValues(
  definition: SlideTypeDefinition,
  emptyClientCarousels = false,
): SlideFieldValues {
  return Object.fromEntries(
    definition.fields
      .filter((field) => !field.adminOnly && field.type !== "html")
      .map((field) => {
        if (field.defaultValue !== undefined) return [field.key, field.defaultValue];
        if (["image", "logo"].includes(field.type)) return [field.key, ""];
        if (field.type === "video") return [field.key, ""];
        if (field.type === "url") return [field.key, "https://qassist.qa"];
        if (field.type === "youtube")
          return [
            field.key,
            {
              url: "https://www.youtube.com/watch?v=M7lc1UVf-VE",
              playbackMode: "loop",
              shuffle: false,
            },
          ];
        if (field.type === "youtubeList") return [field.key, { urls: [], shuffle: false }];
        if (field.type === "wordpressPosts")
          return [
            field.key,
            emptyClientCarousels
              ? ""
              : {
                  mode: "wordpress",
                  apiUrl: "https://example.com/wp-json",
                  postType: "posts",
                  limit: 10,
                  posts: [
                    {
                      title: "Latest WordPress post",
                      description: "Post excerpts appear here and cycle automatically.",
                    },
                    {
                      title: "Another update",
                      description: "Style the title and excerpt separately in the builder.",
                    },
                  ],
                },
          ];
        if (field.type === "phone") return [field.key, "+974 5555 1234"];
        if (field.type === "whatsapp")
          return [field.key, { phone: "+97455551234", message: "Hello from Q-Assist TV" }];
        if (field.type === "tickerContent")
          return [
            field.key,
            { mode: "manual", headlines: "First headline\nSecond headline\nThird headline" },
          ];
        if (field.type === "imageCarousel")
          return [
            field.key,
            emptyClientCarousels ? [] : ["/demo-logo.svg", "/demo-logo.svg", "/demo-logo.svg"],
          ];
        if (field.type === "textCarousel")
          return [
            field.key,
            emptyClientCarousels
              ? []
              : [
                  { title: "Welcome", description: "A clear supporting description" },
                  { title: "Explore", description: "A second carousel item" },
                ],
          ];
        if (field.type === "contentCarousel")
          return [
            field.key,
            emptyClientCarousels
              ? []
              : [
                  {
                    image: "/demo-logo.svg",
                    title: "Welcome",
                    description: "A clear supporting description",
                  },
                  {
                    image: "/demo-logo.svg",
                    title: "Explore",
                    description: "A second carousel item",
                  },
                ],
          ];
        if (field.type === "number" || field.type === "fontSize") return [field.key, 42];
        if (field.type === "boolean") return [field.key, true];
        return [
          field.key,
          field.type === "textarea"
            ? "Clear communication, beautifully presented on every screen."
            : field.label,
        ];
      }),
  );
}

export function AdminSlideTypesPage() {
  const queryClient = useQueryClient();
  const duplicatingIds = useRef(new Set<string>());
  const [builderOpen, setBuilderOpen] = useState(false);
  const [selected, setSelected] = useState<SlideTypeDefinition | null>(null);
  const [editing, setEditing] = useState<SlideTypeRow | null>(null);
  const [builderError, setBuilderError] = useState("");
  const [transferError, setTransferError] = useState<Error | null>(null);
  const query = useQuery({
    queryKey: ["admin", "slide-types"],
    queryFn: () => api<SlideTypeRow[]>("/api/v1/admin/slide-types"),
  });
  const clientsQuery = useQuery({
    queryKey: ["admin", "clients"],
    queryFn: () => api<ClientRow[]>("/api/v1/admin/clients"),
  });
  const create = useMutation({
    mutationFn: (definition: SlideTypeDefinition) =>
      api("/api/v1/admin/slide-types", { method: "POST", body: JSON.stringify(definition) }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ["admin", "slide-types"] });
      setBuilderOpen(false);
      setBuilderError("");
    },
    onError: (error) =>
      setBuilderError(error instanceof Error ? error.message : "Slide type could not be created."),
  });
  const update = useMutation({
    mutationFn: (definition: SlideTypeDefinition) =>
      api(`/api/v1/admin/slide-types/${editing?.id ?? ""}`, {
        method: "PUT",
        body: JSON.stringify(definition),
      }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ["admin", "slide-types"] });
      setEditing(null);
      setBuilderError("");
    },
    onError: (error) =>
      setBuilderError(error instanceof Error ? error.message : "Slide type could not be updated."),
  });
  const archive = useMutation({
    mutationFn: (id: string) => api(`/api/v1/admin/slide-types/${id}`, { method: "DELETE" }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["admin", "slide-types"] }),
  });
  const duplicate = useMutation({
    mutationFn: ({ id, key }: { id: string; key: string }) =>
      api(`/api/v1/admin/slide-types/${id}/duplicate`, {
        method: "POST",
        headers: { "Idempotency-Key": key },
      }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["admin", "slide-types"] }),
    onSettled: (_data, _error, variables) => duplicatingIds.current.delete(variables.id),
  });
  function duplicateSlideType(id: string): void {
    if (duplicatingIds.current.has(id)) return;
    duplicatingIds.current.add(id);
    duplicate.mutate({ id, key: crypto.randomUUID() });
  }
  const exportSlideType = useMutation({
    mutationFn: (item: SlideTypeRow) => api<unknown>(`/api/v1/admin/slide-types/${item.id}/export`),
    onSuccess: (document, item) => {
      setTransferError(null);
      downloadJsonFile(document, `${portableFilename(item.name)}.slide-type.json`);
    },
    onError: (error) =>
      setTransferError(
        error instanceof Error ? error : new Error("The slide type could not be exported."),
      ),
  });
  const importSlideType = useMutation({
    mutationFn: (document: unknown) =>
      api("/api/v1/admin/slide-types/import", {
        method: "POST",
        body: JSON.stringify(document),
      }),
    onSuccess: async () => {
      setTransferError(null);
      await queryClient.invalidateQueries({ queryKey: ["admin", "slide-types"] });
    },
    onError: (error) =>
      setTransferError(
        error instanceof Error ? error : new Error("The slide type could not be imported."),
      ),
  });
  async function handleSlideTypeImport(file: File | undefined): Promise<void> {
    if (!file) return;
    try {
      setTransferError(null);
      importSlideType.mutate(await readJsonFile(file));
    } catch (error) {
      setTransferError(error instanceof Error ? error : new Error("The file could not be read."));
    }
  }
  return (
    <div className="page-stack">
      <section className="page-actions">
        <div>
          <h2>Data-driven slide types</h2>
          <p>
            Every template is safe JSON, versioned, and rendered identically in previews and
            players.
          </p>
        </div>
        <div className="page-action-buttons">
          <label
            className={`button button--secondary ${importSlideType.isPending ? "is-disabled" : ""}`}
          >
            <Upload size={17} /> {importSlideType.isPending ? "Importing…" : "Import slide type"}
            <input
              className="visually-hidden"
              type="file"
              accept="application/json,.json"
              disabled={importSlideType.isPending}
              onChange={(event) => {
                void handleSlideTypeImport(event.target.files?.[0]);
                event.currentTarget.value = "";
              }}
            />
          </label>
          <button
            className="button button--primary"
            onClick={() => {
              setBuilderError("");
              setBuilderOpen(true);
            }}
          >
            <WandSparkles size={17} /> Build slide type
          </button>
        </div>
      </section>
      <ErrorNotice error={transferError} />
      <div className="template-grid">
        {query.isLoading ? (
          <PageLoading />
        ) : query.error ? (
          <ErrorNotice error={query.error} />
        ) : (
          query.data?.map((item) => (
            <article className="template-card" key={item.id}>
              <button
                className="template-preview"
                onClick={() => setSelected(item.definition)}
                aria-label={`Preview ${item.name}`}
              >
                <ScaledSlideRenderer
                  definition={item.definition}
                  values={sampleValues(item.definition)}
                  interactive={false}
                />
              </button>
              <div className="template-copy">
                <div>
                  <span
                    className={`status-pill ${
                      item.status === "published" ? "status-pill--good" : "status-pill--neutral"
                    }`}
                  >
                    {item.status === "published" ? "Published" : "Draft"}
                  </span>
                  <span className="status-pill status-pill--good">
                    v{item.latestVersion ?? item.definition.version}
                  </span>
                  <span className="status-pill status-pill--neutral">
                    {(item.definition.purpose ?? "slide") === "overlay"
                      ? "Playlist overlay"
                      : "Rotating slide"}
                  </span>
                  <span className="muted">{item.definition.fields.length} fields</span>
                  <span className="status-pill status-pill--neutral">
                    {item.definition.availability.global
                      ? "All clients"
                      : `${item.definition.availability.clientIds.length} selected client${
                          item.definition.availability.clientIds.length === 1 ? "" : "s"
                        }`}
                  </span>
                </div>
                <h3>{item.name}</h3>
                <p>{item.description}</p>
                <div className="card-inline-actions">
                  <button className="text-button" onClick={() => setSelected(item.definition)}>
                    Preview <ArrowRight size={15} />
                  </button>
                  <button
                    className="text-button"
                    onClick={() => {
                      setBuilderError("");
                      setEditing(item);
                    }}
                  >
                    <Pencil size={15} /> Edit design
                  </button>
                  <button
                    className="text-button"
                    disabled={duplicate.isPending && duplicate.variables?.id === item.id}
                    onClick={() => duplicateSlideType(item.id)}
                  >
                    <Copy size={15} />{" "}
                    {duplicate.isPending && duplicate.variables?.id === item.id
                      ? "Duplicating…"
                      : "Duplicate"}
                  </button>
                  <button
                    className="text-button"
                    disabled={
                      exportSlideType.isPending && exportSlideType.variables?.id === item.id
                    }
                    onClick={() => exportSlideType.mutate(item)}
                  >
                    <Download size={15} />{" "}
                    {exportSlideType.isPending && exportSlideType.variables?.id === item.id
                      ? "Exporting…"
                      : "Export"}
                  </button>
                  <button
                    className="text-button text-button--danger"
                    onClick={() => {
                      if (window.confirm(`Remove ${item.name}?`)) archive.mutate(item.id);
                    }}
                  >
                    <Trash2 size={15} /> Remove
                  </button>
                </div>
              </div>
            </article>
          ))
        )}
      </div>
      <ErrorNotice error={archive.error ?? duplicate.error} />
      {selected ? (
        <Modal
          title={selected.name}
          description="Shared renderer preview at the selected desktop or signage resolution."
          onClose={() => setSelected(null)}
          size="large"
        >
          <div className="large-preview">
            <ScreenPreview definition={selected} values={sampleValues(selected)} />
          </div>
        </Modal>
      ) : null}
      {builderOpen ? (
        <Modal
          title="Visual template builder"
          description="Choose the layout, colors, and content fields while the builder generates a safe versioned definition."
          onClose={() => {
            setBuilderOpen(false);
            setBuilderError("");
          }}
          size="fullscreen"
        >
          <TemplateGuiBuilder
            initialDefinition={{
              ...builtInSlideTypes[6]!,
              key: "new-announcement",
              name: "New announcement",
              version: 1,
              status: "draft",
            }}
            mode="create"
            saving={create.isPending}
            clientOptions={(clientsQuery.data ?? [])
              .filter((client) => client.status !== "archived")
              .map((client) => ({ id: client.id, name: client.name }))}
            error={builderError}
            onCancel={() => {
              setBuilderOpen(false);
              setBuilderError("");
            }}
            onSave={(definition) => create.mutate(definition)}
          />
        </Modal>
      ) : null}
      {editing ? (
        <Modal
          title={`Edit ${editing.name}`}
          description="The layout, bounded elements, fields, and canvas are one versioned design. Saving creates a new version so published versions remain immutable."
          onClose={() => {
            setEditing(null);
            setBuilderError("");
          }}
          size="fullscreen"
        >
          <TemplateGuiBuilder
            initialDefinition={editing.definition}
            mode="edit"
            saving={update.isPending}
            clientOptions={(clientsQuery.data ?? [])
              .filter((client) => client.status !== "archived")
              .map((client) => ({ id: client.id, name: client.name }))}
            error={builderError}
            onCancel={() => {
              setEditing(null);
              setBuilderError("");
            }}
            onSave={(definition) => update.mutate(definition)}
          />
        </Modal>
      ) : null}
    </div>
  );
}

interface SlideRow {
  id: string;
  playlistId: string;
  slideTypeVersionId: string;
  name: string;
  status: string;
  fieldValues: SlideFieldValues;
  defaultDurationMs: number;
  advanceOnVideoEnd: boolean;
  videoLoop: boolean;
  videoMuted: boolean;
  updatedAt: string;
  definition: SlideTypeDefinition;
}

interface ClientRssSource {
  id: string;
  name: string;
  url: string;
  headlines: string[];
}

function tickerPreviewValues(
  values: SlideFieldValues,
  rssSources: ClientRssSource[],
): SlideFieldValues {
  return Object.fromEntries(
    Object.entries(values).map(([key, value]) => {
      if (
        value &&
        typeof value === "object" &&
        "mode" in value &&
        value.mode === "rss" &&
        "rssSourceId" in value &&
        typeof value.rssSourceId === "string"
      ) {
        return [
          key,
          {
            ...value,
            headlines:
              rssSources.find((source) => source.id === value.rssSourceId)?.headlines ?? [],
          },
        ];
      }
      return [key, value];
    }),
  );
}

function DynamicField({
  field,
  value,
  onChange,
  assets = [],
  rssSources = [],
}: {
  field: FieldDefinition;
  value: unknown;
  onChange(value: unknown): void;
  assets?: Array<{ id: string; name: string; publicUrl: string; mimeType: string }>;
  rssSources?: ClientRssSource[];
}) {
  const [pendingAssetId, setPendingAssetId] = useState("");
  const [pendingTitle, setPendingTitle] = useState("");
  const [pendingDescription, setPendingDescription] = useState("");
  const imageAssets = assets.filter((asset) => asset.mimeType.startsWith("image/"));
  const moveCarouselItem = (items: unknown[], index: number, delta: number): void => {
    const destination = index + delta;
    if (destination < 0 || destination >= items.length) return;
    const reordered = [...items];
    const [moved] = reordered.splice(index, 1);
    reordered.splice(destination, 0, moved);
    onChange(reordered);
  };
  const assetValue = (assetId: string) => {
    const asset = imageAssets.find((candidate) => candidate.id === assetId);
    return asset ? { id: asset.id, url: asset.publicUrl, mimeType: asset.mimeType } : undefined;
  };

  if (field.type === "wordpressPosts")
    return <WordPressPostsField field={field} value={value} onChange={onChange} />;
  if (field.type === "imageCarousel") {
    const items: unknown[] = Array.isArray(value) ? (value as unknown[]) : [];
    const addAsset = assetValue(pendingAssetId);
    return (
      <fieldset className="dynamic-field-group carousel-field-editor">
        <legend>
          {field.label}
          {field.required ? " *" : ""}
        </legend>
        <div className="carousel-field-editor__add-row">
          <label>
            <span>Add an image from assets</span>
            <select
              value={pendingAssetId}
              onChange={(event) => setPendingAssetId(event.target.value)}
            >
              <option value="">Choose an image…</option>
              {imageAssets.map((asset) => (
                <option key={asset.id} value={asset.id}>
                  {asset.name}
                </option>
              ))}
            </select>
          </label>
          <button
            type="button"
            className="button button--secondary"
            disabled={!addAsset || items.length >= 100}
            onClick={() => {
              if (!addAsset) return;
              onChange([...items, addAsset]);
              setPendingAssetId("");
            }}
          >
            <Plus size={16} /> Add image
          </button>
        </div>
        <div className="carousel-field-editor__items">
          {items.map((item, index) => {
            const current =
              item && typeof item === "object"
                ? (item as { id?: string; url?: string; mimeType?: string })
                : {};
            const asset = imageAssets.find((candidate) => candidate.id === current.id);
            return (
              <article
                className="carousel-field-item carousel-field-item--image"
                key={`${current.id ?? "image"}-${index}`}
              >
                {current.url ? <img src={current.url} alt="" /> : <FileImage size={30} />}
                <strong>{asset?.name ?? `Image ${index + 1}`}</strong>
                <div className="carousel-field-item__actions">
                  <button
                    type="button"
                    className="icon-button"
                    aria-label={`Move image ${index + 1} up`}
                    disabled={index === 0}
                    onClick={() => moveCarouselItem(items, index, -1)}
                  >
                    <ArrowUp size={15} />
                  </button>
                  <button
                    type="button"
                    className="icon-button"
                    aria-label={`Move image ${index + 1} down`}
                    disabled={index === items.length - 1}
                    onClick={() => moveCarouselItem(items, index, 1)}
                  >
                    <ArrowDown size={15} />
                  </button>
                  <button
                    type="button"
                    className="icon-button icon-button--danger"
                    aria-label={`Remove image ${index + 1}`}
                    onClick={() => onChange(items.filter((_, itemIndex) => itemIndex !== index))}
                  >
                    <Trash2 size={15} />
                  </button>
                </div>
              </article>
            );
          })}
          {!items.length ? <p className="muted">No carousel images added yet.</p> : null}
        </div>
        <small className="field-help">
          {items.length}/100 images. Use the arrow buttons to set the playback order.
        </small>
      </fieldset>
    );
  }

  if (field.type === "textCarousel" || field.type === "contentCarousel") {
    const items: unknown[] = Array.isArray(value) ? (value as unknown[]) : [];
    const needsImage = field.type === "contentCarousel";
    const pendingImage = assetValue(pendingAssetId);
    const canAdd =
      Boolean(pendingTitle.trim()) && (!needsImage || Boolean(pendingImage)) && items.length < 100;
    return (
      <fieldset className="dynamic-field-group carousel-field-editor">
        <legend>
          {field.label}
          {field.required ? " *" : ""}
        </legend>
        <div className="carousel-field-editor__new-card">
          {needsImage ? (
            <label>
              <span>Image</span>
              <select
                value={pendingAssetId}
                onChange={(event) => setPendingAssetId(event.target.value)}
              >
                <option value="">Choose an image…</option>
                {imageAssets.map((asset) => (
                  <option key={asset.id} value={asset.id}>
                    {asset.name}
                  </option>
                ))}
              </select>
            </label>
          ) : null}
          <label>
            <span>Title</span>
            <input
              value={pendingTitle}
              maxLength={240}
              onChange={(event) => setPendingTitle(event.target.value)}
            />
          </label>
          <label>
            <span>Description</span>
            <textarea
              value={pendingDescription}
              maxLength={2000}
              onChange={(event) => setPendingDescription(event.target.value)}
            />
          </label>
          <button
            type="button"
            className="button button--secondary"
            disabled={!canAdd}
            onClick={() => {
              if (!canAdd) return;
              onChange([
                ...items,
                {
                  ...(needsImage && pendingImage ? { image: pendingImage } : {}),
                  title: pendingTitle.trim(),
                  description: pendingDescription.trim(),
                },
              ]);
              setPendingAssetId("");
              setPendingTitle("");
              setPendingDescription("");
            }}
          >
            <Plus size={16} /> Add carousel item
          </button>
        </div>
        <div className="carousel-field-editor__items">
          {items.map((item, index) => {
            const current =
              item && typeof item === "object"
                ? (item as {
                    image?: { id?: string; url?: string; mimeType?: string };
                    title?: string;
                    description?: string;
                  })
                : {};
            return (
              <article className="carousel-field-item" key={index}>
                {needsImage ? (
                  <label>
                    <span>Image {index + 1}</span>
                    <select
                      required
                      value={current.image?.id ?? ""}
                      onChange={(event) => {
                        const image = assetValue(event.target.value);
                        onChange(
                          items.map((candidate, itemIndex) =>
                            itemIndex === index && candidate && typeof candidate === "object"
                              ? { ...candidate, image }
                              : candidate,
                          ),
                        );
                      }}
                    >
                      <option value="">Choose an image…</option>
                      {imageAssets.map((asset) => (
                        <option key={asset.id} value={asset.id}>
                          {asset.name}
                        </option>
                      ))}
                    </select>
                  </label>
                ) : null}
                <label>
                  <span>Title {index + 1}</span>
                  <input
                    required
                    value={current.title ?? ""}
                    maxLength={240}
                    onChange={(event) =>
                      onChange(
                        items.map((candidate, itemIndex) =>
                          itemIndex === index && candidate && typeof candidate === "object"
                            ? { ...candidate, title: event.target.value }
                            : candidate,
                        ),
                      )
                    }
                  />
                </label>
                <label>
                  <span>Description {index + 1}</span>
                  <textarea
                    value={current.description ?? ""}
                    maxLength={2000}
                    onChange={(event) =>
                      onChange(
                        items.map((candidate, itemIndex) =>
                          itemIndex === index && candidate && typeof candidate === "object"
                            ? { ...candidate, description: event.target.value }
                            : candidate,
                        ),
                      )
                    }
                  />
                </label>
                <div className="carousel-field-item__actions">
                  <button
                    type="button"
                    className="icon-button"
                    aria-label={`Move carousel item ${index + 1} up`}
                    disabled={index === 0}
                    onClick={() => moveCarouselItem(items, index, -1)}
                  >
                    <ArrowUp size={15} />
                  </button>
                  <button
                    type="button"
                    className="icon-button"
                    aria-label={`Move carousel item ${index + 1} down`}
                    disabled={index === items.length - 1}
                    onClick={() => moveCarouselItem(items, index, 1)}
                  >
                    <ArrowDown size={15} />
                  </button>
                  <button
                    type="button"
                    className="icon-button icon-button--danger"
                    aria-label={`Remove carousel item ${index + 1}`}
                    onClick={() => onChange(items.filter((_, itemIndex) => itemIndex !== index))}
                  >
                    <Trash2 size={15} />
                  </button>
                </div>
              </article>
            );
          })}
          {!items.length ? <p className="muted">No carousel items added yet.</p> : null}
        </div>
        <small className="field-help">
          {items.length}/100 items. Titles are required; descriptions are optional.
        </small>
      </fieldset>
    );
  }

  if (field.type === "boolean")
    return (
      <label className="toggle-field">
        <input
          type="checkbox"
          checked={Boolean(value)}
          onChange={(event) => onChange(event.target.checked)}
        />
        <span>{field.label}</span>
      </label>
    );
  if (field.type === "textarea" || field.type === "richText" || field.type === "html")
    return (
      <label>
        <span>
          {field.label}
          {field.required ? " *" : ""}
        </span>
        <textarea
          className={field.type === "html" ? "html-source-input" : undefined}
          value={typeof value === "string" ? value : ""}
          placeholder={field.placeholder}
          required={field.required}
          onChange={(event) => onChange(event.target.value)}
          maxLength={field.maxLength}
        />
      </label>
    );
  if (field.type === "whatsapp") {
    const current =
      value && typeof value === "object"
        ? (value as { phone?: string; message?: string })
        : { phone: "", message: "" };
    return (
      <fieldset className="dynamic-field-group">
        <legend>
          {field.label}
          {field.required ? " *" : ""}
        </legend>
        <label>
          <span>WhatsApp number</span>
          <input
            type="tel"
            value={current.phone ?? ""}
            inputMode="tel"
            placeholder="+974 5555 1234"
            onChange={(event) => onChange({ ...current, phone: event.target.value })}
          />
        </label>
        <label>
          <span>Prefilled message</span>
          <textarea
            value={current.message ?? ""}
            maxLength={1000}
            placeholder="Hello, I would like more information."
            onChange={(event) => onChange({ ...current, message: event.target.value })}
          />
        </label>
        {field.helpText ? <small className="field-help">{field.helpText}</small> : null}
      </fieldset>
    );
  }
  if (field.type === "vcard") {
    const current =
      value && typeof value === "object"
        ? (value as {
            fullName?: string;
            organization?: string;
            title?: string;
            phone?: string;
            email?: string;
            website?: string;
          })
        : {};
    const updateContact = (patch: Partial<typeof current>): void =>
      onChange({ ...current, ...patch });
    return (
      <fieldset className="dynamic-field-group">
        <legend>
          {field.label}
          {field.required ? " *" : ""}
        </legend>
        <div className="form-grid">
          <label>
            <span>Full name</span>
            <input
              value={current.fullName ?? ""}
              required={field.required}
              maxLength={200}
              onChange={(event) => updateContact({ fullName: event.target.value })}
            />
          </label>
          <label>
            <span>Organization</span>
            <input
              value={current.organization ?? ""}
              maxLength={200}
              onChange={(event) => updateContact({ organization: event.target.value })}
            />
          </label>
          <label>
            <span>Job title</span>
            <input
              value={current.title ?? ""}
              maxLength={200}
              onChange={(event) => updateContact({ title: event.target.value })}
            />
          </label>
          <label>
            <span>Phone</span>
            <input
              type="tel"
              value={current.phone ?? ""}
              maxLength={25}
              onChange={(event) => updateContact({ phone: event.target.value })}
            />
          </label>
          <label>
            <span>Email</span>
            <input
              type="email"
              value={current.email ?? ""}
              maxLength={254}
              onChange={(event) => updateContact({ email: event.target.value })}
            />
          </label>
          <label>
            <span>Website</span>
            <input
              type="url"
              value={current.website ?? ""}
              maxLength={2000}
              placeholder="https://example.com"
              onChange={(event) => updateContact({ website: event.target.value })}
            />
          </label>
        </div>
        {field.helpText ? <small className="field-help">{field.helpText}</small> : null}
      </fieldset>
    );
  }
  if (field.type === "tickerContent") {
    const current =
      value && typeof value === "object" && "mode" in value
        ? (value as { mode: "manual" | "rss"; headlines?: string; rssSourceId?: string })
        : { mode: "manual" as const, headlines: "" };
    return (
      <fieldset className="dynamic-field-group">
        <legend>
          {field.label}
          {field.required ? " *" : ""}
        </legend>
        <label>
          <span>Ticker source</span>
          <select
            value={current.mode}
            onChange={(event) =>
              onChange(
                event.target.value === "rss"
                  ? { mode: "rss", rssSourceId: rssSources[0]?.id ?? "" }
                  : { mode: "manual", headlines: "" },
              )
            }
          >
            <option value="manual">Write headlines manually</option>
            <option value="rss" disabled={!rssSources.length}>
              Choose an administrator RSS feed
            </option>
          </select>
        </label>
        {current.mode === "rss" ? (
          <label>
            <span>RSS feed</span>
            <select
              value={current.rssSourceId ?? ""}
              required={field.required}
              onChange={(event) => onChange({ mode: "rss", rssSourceId: event.target.value })}
            >
              <option value="">Choose an available feed…</option>
              {rssSources.map((source) => (
                <option key={source.id} value={source.id}>
                  {source.name}
                </option>
              ))}
            </select>
          </label>
        ) : (
          <label>
            <span>Headlines</span>
            <textarea
              value={current.headlines ?? ""}
              maxLength={12_000}
              placeholder="One headline per line"
              required={field.required}
              onChange={(event) => onChange({ mode: "manual", headlines: event.target.value })}
            />
          </label>
        )}
        {!rssSources.length ? (
          <small className="field-help">
            No RSS feeds are currently available to this client. Ask the platform administrator to
            assign one, or use manual headlines.
          </small>
        ) : null}
      </fieldset>
    );
  }
  if (field.type === "multiSelect")
    return (
      <fieldset className="choice-list">
        <legend>{field.label}</legend>
        {field.options?.map((option) => {
          const selected = Array.isArray(value) && value.includes(String(option.value));
          return (
            <label key={String(option.value)}>
              <input
                type="checkbox"
                checked={selected}
                onChange={(event) => {
                  const current = Array.isArray(value) ? value.map(String) : [];
                  onChange(
                    event.target.checked
                      ? [...current, String(option.value)]
                      : current.filter((item) => item !== String(option.value)),
                  );
                }}
              />
              <span>{option.label}</span>
            </label>
          );
        })}
      </fieldset>
    );
  const semanticOptions =
    field.options?.map((option) => ({ label: option.label, value: String(option.value) })) ??
    (field.type === "fontFamily"
      ? slideFontOptions.map((option) => ({ label: option, value: option }))
      : field.type === "alignment"
        ? alignmentOptions.map((option) => ({
            label: option[0]!.toUpperCase() + option.slice(1),
            value: option,
          }))
        : field.type === "position"
          ? positionOptions.map((option) => ({
              label: option.replaceAll("-", " "),
              value: option,
            }))
          : []);
  if (
    field.type === "select" ||
    field.type === "fontFamily" ||
    field.type === "alignment" ||
    field.type === "position"
  )
    return (
      <label>
        <span>{field.label}</span>
        <select
          value={typeof value === "string" ? value : ""}
          required={field.required}
          onChange={(event) => onChange(event.target.value)}
        >
          <option value="">Choose…</option>
          {semanticOptions.map((option) => (
            <option key={option.value} value={option.value}>
              {option.label}
            </option>
          ))}
        </select>
      </label>
    );
  if (field.type === "youtube") {
    const youtube =
      typeof value === "string"
        ? { url: value, playbackMode: "loop" as const, shuffle: false, controllerEnabled: false }
        : value && typeof value === "object" && "url" in value
          ? {
              url: typeof value.url === "string" ? value.url : "",
              playbackMode:
                "playbackMode" in value &&
                (value.playbackMode === "once" || value.playbackMode === "recommended")
                  ? value.playbackMode
                  : ("loop" as const),
              shuffle: "shuffle" in value && value.shuffle === true,
              controllerEnabled: "controllerEnabled" in value && value.controllerEnabled === true,
            }
          : { url: "", playbackMode: "loop" as const, shuffle: false, controllerEnabled: false };
    const isPlaylist = /[?&]list=/.test(youtube.url);
    return (
      <div className="form-stack">
        <label>
          <span>{field.label}</span>
          <input
            type="url"
            value={youtube.url}
            required={field.required}
            placeholder="YouTube video or playlist URL"
            onChange={(event) => onChange({ ...youtube, url: event.target.value })}
          />
        </label>
        <label>
          <span>Playback after the video ends</span>
          <select
            value={isPlaylist ? "loop" : youtube.playbackMode}
            disabled={isPlaylist}
            onChange={(event) =>
              onChange({
                ...youtube,
                playbackMode: event.target.value as "loop" | "once" | "recommended",
              })
            }
          >
            <option value="loop">Loop this video</option>
            <option value="once">Play once</option>
            <option value="recommended">Continue to YouTube recommendations</option>
          </select>
        </label>
        {isPlaylist ? (
          <>
            <label className="checkbox-field">
              <input
                type="checkbox"
                checked={youtube.shuffle}
                onChange={(event) => onChange({ ...youtube, shuffle: event.target.checked })}
              />
              <span>Shuffle playlist order</span>
            </label>
            <p className="field-help">YouTube playlists always loop.</p>
          </>
        ) : null}
        <button
          type="button"
          className={`button ${youtube.controllerEnabled ? "button--primary" : "button--secondary"}`}
          onClick={() => onChange({ ...youtube, controllerEnabled: !youtube.controllerEnabled })}
        >
          <Gamepad2 size={16} />{" "}
          {youtube.controllerEnabled ? "Added To Controller" : "Add To Controller"}
        </button>
      </div>
    );
  }
  if (field.type === "youtubeList") {
    const list =
      value && typeof value === "object" && "urls" in value && Array.isArray(value.urls)
        ? {
            urls: value.urls.filter((url): url is string => typeof url === "string"),
            shuffle: "shuffle" in value && value.shuffle === true,
            controllerEnabled: "controllerEnabled" in value && value.controllerEnabled === true,
          }
        : { urls: [], shuffle: false, controllerEnabled: false };
    return (
      <fieldset className="form-stack">
        <legend>{field.label}</legend>
        <label>
          <span>YouTube video URLs, one per line</span>
          <textarea
            value={list.urls.join("\n")}
            required={field.required}
            placeholder="https://www.youtube.com/watch?v=…"
            onChange={(event) =>
              onChange({
                ...list,
                urls: event.target.value
                  .split(/\r?\n/)
                  .map((url) => url.trim())
                  .filter(Boolean),
              })
            }
          />
        </label>
        <label className="checkbox-field">
          <input
            type="checkbox"
            checked={list.shuffle}
            onChange={(event) => onChange({ ...list, shuffle: event.target.checked })}
          />
          <span>Shuffle before playback</span>
        </label>
        <p className="field-help">The list loops continuously after the final video.</p>
        <button
          type="button"
          className={`button ${list.controllerEnabled ? "button--primary" : "button--secondary"}`}
          onClick={() => onChange({ ...list, controllerEnabled: !list.controllerEnabled })}
        >
          <Gamepad2 size={16} />{" "}
          {list.controllerEnabled ? "Added To Controller" : "Add To Controller"}
        </button>
      </fieldset>
    );
  }
  if (["image", "logo", "video"].includes(field.type)) {
    const compatible = assets.filter((asset) =>
      field.type === "video"
        ? asset.mimeType.startsWith("video/")
        : asset.mimeType.startsWith("image/"),
    );
    const selectedAssetId =
      value && typeof value === "object" && "id" in value && typeof value.id === "string"
        ? value.id
        : "";
    return (
      <div className="media-field-group">
        <label>
          <span>
            {field.label}
            {field.required ? " *" : ""}
          </span>
          <select
            value={selectedAssetId}
            required={field.required}
            onChange={(event) => {
              const asset = compatible.find((item) => item.id === event.target.value);
              onChange(
                asset ? { id: asset.id, url: asset.publicUrl, mimeType: asset.mimeType } : "",
              );
            }}
          >
            <option value="">Choose from asset library…</option>
            {compatible.map((asset) => (
              <option key={asset.id} value={asset.id}>
                {asset.name}
              </option>
            ))}
          </select>
        </label>
      </div>
    );
  }
  const type =
    field.type === "number" || field.type === "fontSize"
      ? "number"
      : field.type === "date"
        ? "date"
        : field.type === "time"
          ? "time"
          : field.type === "datetime"
            ? "datetime-local"
            : field.type === "color"
              ? "color"
              : field.type === "phone"
                ? "tel"
                : field.type === "url" || ["image", "logo", "video"].includes(field.type)
                  ? "url"
                  : "text";
  return (
    <label>
      <span>
        {field.label}
        {field.required ? " *" : ""}
      </span>
      <input
        type={type}
        value={typeof value === "string" || typeof value === "number" ? value : ""}
        required={field.required}
        placeholder={
          ["image", "logo", "video"].includes(field.type)
            ? "Paste a versioned asset URL"
            : field.type === "phone"
              ? "+974 5555 1234"
              : field.placeholder
        }
        inputMode={field.type === "phone" ? "tel" : undefined}
        min={field.min}
        max={field.max}
        maxLength={field.maxLength}
        onChange={(event) =>
          onChange(type === "number" ? event.target.valueAsNumber : event.target.value)
        }
      />
    </label>
  );
}

interface ClientSlidesPageProps {
  embedded?: boolean;
  playlistId?: string | undefined;
  playlistName?: string | undefined;
  creationPlaylistName?: string | undefined;
  playlistSlideIds?: string[] | undefined;
  slidePlaylistAssignments?: Record<string, { id: string; name: string }> | undefined;
  playlistItemSettings?:
    | Record<
        string,
        {
          entranceAnimation: string | null;
          exitAnimation: string | null;
        }
      >
    | undefined;
  playlists?: Array<{ id: string; name: string }> | undefined;
  onMoveToPlaylist?: ((slideId: string, playlistId: string) => Promise<void>) | undefined;
  onReorderSlides?: ((slideIds: string[]) => Promise<void>) | undefined;
  onUpdateAnimations?:
    | ((slideId: string, entranceAnimation: string, exitAnimation: string) => Promise<void>)
    | undefined;
}

interface SlideFormInput {
  name: string;
  defaultDurationMs: number;
  advanceOnVideoEnd: boolean;
  videoLoop: boolean;
  videoMuted: boolean;
}

export function ClientSlidesPage({
  embedded = false,
  playlistId,
  playlistName,
  creationPlaylistName,
  playlistSlideIds,
  slidePlaylistAssignments,
  playlistItemSettings,
  playlists = [],
  onMoveToPlaylist,
  onReorderSlides,
  onUpdateAnimations,
}: ClientSlidesPageProps = {}) {
  const queryClient = useQueryClient();
  const duplicatingIds = useRef(new Set<string>());
  const [creating, setCreating] = useState(false);
  const [preview, setPreview] = useState<SlideRow | null>(null);
  const [editing, setEditing] = useState<SlideRow | null>(null);
  const [selectedVersionId, setSelectedVersionId] = useState("");
  const [values, setValues] = useState<SlideFieldValues>({});
  const [formError, setFormError] = useState("");
  const [movingSlide, setMovingSlide] = useState<SlideRow | null>(null);
  const [moveTargetId, setMoveTargetId] = useState("");
  const [moveError, setMoveError] = useState<Error | null>(null);
  const [movePending, setMovePending] = useState(false);
  const [orderedSlideIds, setOrderedSlideIds] = useState<string[]>(playlistSlideIds ?? []);
  const [draggedSlideId, setDraggedSlideId] = useState("");
  const draggedSlideIdRef = useRef("");
  const [dropSlideId, setDropSlideId] = useState("");
  const [dropPlacement, setDropPlacement] = useState<"before" | "after">("before");
  const [orderAnnouncement, setOrderAnnouncement] = useState("");
  const [transferError, setTransferError] = useState<Error | null>(null);
  const slidesQuery = useQuery({
    queryKey: ["client", "slides"],
    queryFn: () => api<SlideRow[]>("/api/v1/client/slides"),
  });
  const typesQuery = useQuery({
    queryKey: ["client", "slide-types"],
    queryFn: () => api<SlideTypeRow[]>("/api/v1/client/slide-types"),
  });
  const assetsQuery = useQuery({
    queryKey: ["client", "assets"],
    queryFn: () =>
      api<Array<{ id: string; name: string; publicUrl: string; mimeType: string }>>(
        "/api/v1/client/assets",
      ),
  });
  const rssSourcesQuery = useQuery({
    queryKey: ["client", "rss-sources"],
    queryFn: () => api<ClientRssSource[]>("/api/v1/client/rss-sources"),
  });
  const selectedType =
    typesQuery.data?.find((type) => type.versionId === selectedVersionId) ??
    (editing && editing.slideTypeVersionId === selectedVersionId
      ? {
          id: editing.slideTypeVersionId,
          versionId: editing.slideTypeVersionId,
          key: editing.definition.key,
          name: editing.definition.name,
          description: editing.definition.description,
          definition: editing.definition,
        }
      : undefined);
  const { register, handleSubmit, reset, setValue, watch } = useForm<SlideFormInput>({
    defaultValues: {
      name: "",
      defaultDurationMs: 10000,
      advanceOnVideoEnd: false,
      videoLoop: true,
      videoMuted: true,
    },
  });
  const selectedTypeHasVideo = selectedType ? slideTypeHasVideo(selectedType.definition) : false;
  const advanceOnVideoEnd = watch("advanceOnVideoEnd");
  const videoLoop = watch("videoLoop");
  const videoMuted = watch("videoMuted");
  const create = useMutation({
    mutationFn: (input: SlideFormInput) =>
      api<{ id: string }>("/api/v1/client/slides", {
        method: "POST",
        body: JSON.stringify({
          ...input,
          name: undefined,
          playlistId,
          slideTypeVersionId: selectedVersionId,
          fieldValues: values,
          status: "published",
        }),
      }),
    onSuccess: async () => {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ["client", "slides"] }),
        queryClient.invalidateQueries({ queryKey: ["client", "playlists"] }),
      ]);
      setCreating(false);
      setValues({});
      setSelectedVersionId("");
      reset();
    },
    onError: (error) =>
      setFormError(error instanceof Error ? error.message : "Slide could not be created."),
  });
  const update = useMutation({
    mutationFn: (input: SlideFormInput) =>
      api(`/api/v1/client/slides/${editing?.id ?? ""}`, {
        method: "PATCH",
        body: JSON.stringify({
          ...input,
          fieldValues: values,
          status: "published",
        }),
      }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ["client", "slides"] });
      setEditing(null);
      setValues({});
      setSelectedVersionId("");
      reset();
    },
    onError: (error) =>
      setFormError(error instanceof Error ? error.message : "Slide could not be updated."),
  });
  const archive = useMutation({
    mutationFn: (id: string) => api(`/api/v1/client/slides/${id}`, { method: "DELETE" }),
    onSuccess: () =>
      Promise.all([
        queryClient.invalidateQueries({ queryKey: ["client", "slides"] }),
        queryClient.invalidateQueries({ queryKey: ["client", "playlists"] }),
      ]),
  });
  const duplicate = useMutation({
    mutationFn: ({ id, key }: { id: string; key: string }) =>
      api(`/api/v1/client/slides/${id}/duplicate`, {
        method: "POST",
        headers: { "Idempotency-Key": key },
      }),
    onSuccess: () =>
      Promise.all([
        queryClient.invalidateQueries({ queryKey: ["client", "slides"] }),
        queryClient.invalidateQueries({ queryKey: ["client", "playlists"] }),
      ]),
    onSettled: (_data, _error, variables) => duplicatingIds.current.delete(variables.id),
  });
  function duplicateSlide(id: string): void {
    if (duplicatingIds.current.has(id)) return;
    duplicatingIds.current.add(id);
    duplicate.mutate({ id, key: crypto.randomUUID() });
  }
  const exportSlide = useMutation({
    mutationFn: (slide: SlideRow) => api<unknown>(`/api/v1/client/slides/${slide.id}/export`),
    onSuccess: (document, slide) => {
      setTransferError(null);
      downloadJsonFile(document, `${portableFilename(slide.name)}.slide.json`);
    },
    onError: (error) =>
      setTransferError(
        error instanceof Error ? error : new Error("The slide could not be exported."),
      ),
  });
  const importSlide = useMutation({
    mutationFn: (document: unknown) => {
      if (!playlistId) throw new Error("Choose a playlist before importing a slide.");
      return api("/api/v1/client/slides/import", {
        method: "POST",
        body: JSON.stringify({ playlistId, document }),
      });
    },
    onSuccess: async () => {
      setTransferError(null);
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ["client", "slides"] }),
        queryClient.invalidateQueries({ queryKey: ["client", "playlists"] }),
      ]);
    },
    onError: (error) =>
      setTransferError(
        error instanceof Error ? error : new Error("The slide could not be imported."),
      ),
  });
  async function handleSlideImport(file: File | undefined): Promise<void> {
    if (!file) return;
    try {
      setTransferError(null);
      importSlide.mutate(await readJsonFile(file));
    } catch (error) {
      setTransferError(error instanceof Error ? error : new Error("The file could not be read."));
    }
  }
  function selectType(versionId: string): void {
    setSelectedVersionId(versionId);
    const type = typesQuery.data?.find((item) => item.versionId === versionId);
    setValues(type ? sampleValues(type.definition, true) : {});
    if (!type || !slideTypeHasVideo(type.definition)) {
      setValue("advanceOnVideoEnd", false);
      setValue("videoLoop", true);
      setValue("videoMuted", true);
    }
  }
  function openEdit(slide: SlideRow): void {
    setEditing(slide);
    setSelectedVersionId(slide.slideTypeVersionId);
    setValues(slide.fieldValues);
    reset({
      name: slide.name,
      defaultDurationMs: slide.defaultDurationMs,
      advanceOnVideoEnd: slide.advanceOnVideoEnd,
      videoLoop: slide.videoLoop,
      videoMuted: slide.videoMuted,
    });
    setFormError("");
  }
  const visibleSlides =
    playlistSlideIds === undefined
      ? (slidesQuery.data ?? [])
      : orderedSlideIds
          .map((slideId) => slidesQuery.data?.find((slide) => slide.id === slideId))
          .filter((slide): slide is SlideRow => Boolean(slide));
  useEffect(() => {
    setOrderedSlideIds(playlistSlideIds ?? []);
  }, [playlistSlideIds?.join("|")]);
  async function commitReorder(
    slideId: string,
    targetId: string,
    placement: "before" | "after" = "before",
  ): Promise<void> {
    if (!onReorderSlides || slideId === targetId) return;
    const previous = [...orderedSlideIds];
    const next = previous.filter((id) => id !== slideId);
    const targetIndex = next.indexOf(targetId);
    if (targetIndex < 0) return;
    const insertionIndex = targetIndex + (placement === "after" ? 1 : 0);
    next.splice(insertionIndex, 0, slideId);
    setOrderedSlideIds(next);
    setOrderAnnouncement(
      `${slidesQuery.data?.find((slide) => slide.id === slideId)?.name ?? "Slide"} moved to position ${insertionIndex + 1}.`,
    );
    try {
      await onReorderSlides(next);
    } catch {
      setOrderedSlideIds(previous);
      setOrderAnnouncement("The slide order could not be saved.");
    }
  }
  async function moveByKeyboard(slideId: string, direction: -1 | 1): Promise<void> {
    const index = orderedSlideIds.indexOf(slideId);
    const targetId = orderedSlideIds[index + direction];
    if (!targetId) return;
    if (direction > 0) {
      const next = [...orderedSlideIds];
      [next[index], next[index + 1]] = [next[index + 1]!, next[index]!];
      const previous = orderedSlideIds;
      setOrderedSlideIds(next);
      setOrderAnnouncement(`Slide moved to position ${index + 2}.`);
      try {
        await onReorderSlides?.(next);
      } catch {
        setOrderedSlideIds(previous);
      }
      return;
    }
    await commitReorder(slideId, targetId);
  }
  async function moveSlide(): Promise<void> {
    if (!movingSlide || !moveTargetId || !onMoveToPlaylist) return;
    setMovePending(true);
    setMoveError(null);
    try {
      await onMoveToPlaylist(movingSlide.id, moveTargetId);
      setMovingSlide(null);
      setMoveTargetId("");
    } catch (error) {
      setMoveError(error instanceof Error ? error : new Error("The slide could not be moved."));
    } finally {
      setMovePending(false);
    }
  }
  return (
    <div className={embedded ? "playlist-slides-panel" : "page-stack"}>
      <section className="page-actions">
        <div>
          <h2>{playlistName ? `Slides in ${playlistName}` : "Slides"}</h2>
          <p>
            {playlistName
              ? "Arrange these slides directly here. Every slide belongs to exactly one playlist."
              : "Every slide is assigned to one playlist. Move slides between playlists from their cards."}
          </p>
        </div>
        <div className="page-action-buttons">
          <label
            className={`button button--secondary ${
              !playlistId || importSlide.isPending ? "is-disabled" : ""
            }`}
            title={playlistId ? "Import a Q-Assist TV slide" : "Choose a playlist first"}
          >
            <Upload size={17} /> {importSlide.isPending ? "Importing…" : "Import slide"}
            <input
              className="visually-hidden"
              type="file"
              accept="application/json,.json"
              disabled={!playlistId || importSlide.isPending}
              onChange={(event) => {
                void handleSlideImport(event.target.files?.[0]);
                event.currentTarget.value = "";
              }}
            />
          </label>
          <button
            className="button button--primary"
            onClick={() => setCreating(true)}
            disabled={!playlistId}
          >
            <Plus size={17} /> Create slide
          </button>
        </div>
      </section>
      <ErrorNotice error={transferError} />
      {slidesQuery.isLoading ? (
        <PageLoading />
      ) : slidesQuery.error ? (
        <ErrorNotice error={slidesQuery.error} />
      ) : visibleSlides.length ? (
        <>
          <p className="sr-only" aria-live="polite">
            {orderAnnouncement}
          </p>
          <div className="slide-grid">
            {visibleSlides.map((slide, index) => {
              const isOverlay = (slide.definition.purpose ?? "slide") === "overlay";
              const entranceAnimation =
                playlistItemSettings?.[slide.id]?.entranceAnimation ??
                slide.definition.animations.entrance.name;
              const exitAnimation =
                playlistItemSettings?.[slide.id]?.exitAnimation ??
                slide.definition.animations.exit.name;
              return (
                <article
                  className={[
                    "slide-card",
                    draggedSlideId === slide.id ? "is-dragging" : "",
                    dropSlideId === slide.id ? `is-drop-${dropPlacement}` : "",
                  ]
                    .filter(Boolean)
                    .join(" ")}
                  data-slide-id={slide.id}
                  key={slide.id}
                  onDragOver={(event) => {
                    if (!onReorderSlides || isOverlay) return;
                    event.preventDefault();
                    setDropSlideId(slide.id);
                    const bounds = event.currentTarget.getBoundingClientRect();
                    setDropPlacement(
                      event.clientY < bounds.top + bounds.height / 2 ? "before" : "after",
                    );
                  }}
                  onDragLeave={(event) => {
                    if (!event.currentTarget.contains(event.relatedTarget as Node | null))
                      setDropSlideId("");
                  }}
                  onDrop={(event) => {
                    event.preventDefault();
                    const sourceId =
                      event.dataTransfer.getData("text/plain") || draggedSlideIdRef.current;
                    const bounds = event.currentTarget.getBoundingClientRect();
                    const placement =
                      event.clientY < bounds.top + bounds.height / 2 ? "before" : "after";
                    draggedSlideIdRef.current = "";
                    setDraggedSlideId("");
                    setDropSlideId("");
                    if (!isOverlay) void commitReorder(sourceId, slide.id, placement);
                  }}
                >
                  {playlistSlideIds && onReorderSlides && !isOverlay ? (
                    <div className="slide-order-strip">
                      <span>{String(index + 1).padStart(2, "0")}</span>
                      <span
                        className="drag-handle"
                        draggable
                        tabIndex={0}
                        role="slider"
                        aria-label={`Reorder ${slide.name}. Use drag and drop or the arrow keys.`}
                        aria-valuemin={1}
                        aria-valuemax={visibleSlides.length}
                        aria-valuenow={index + 1}
                        title="Drag to reorder. Arrow keys also work."
                        onDragStart={(event) => {
                          draggedSlideIdRef.current = slide.id;
                          setDraggedSlideId(slide.id);
                          event.dataTransfer.effectAllowed = "move";
                          event.dataTransfer.setData("text/plain", slide.id);
                        }}
                        onDragEnd={() => {
                          draggedSlideIdRef.current = "";
                          setDraggedSlideId("");
                          setDropSlideId("");
                        }}
                        onKeyDown={(event) => {
                          if (event.key !== "ArrowUp" && event.key !== "ArrowDown") return;
                          event.preventDefault();
                          void moveByKeyboard(slide.id, event.key === "ArrowUp" ? -1 : 1);
                        }}
                      >
                        <GripVertical size={17} />
                        Drag to reorder
                      </span>
                    </div>
                  ) : null}
                  <button
                    className="slide-thumb"
                    onClick={() => setPreview(slide)}
                    aria-label={`Preview ${slide.name}`}
                  >
                    <ScaledSlideRenderer
                      definition={slide.definition}
                      values={slide.fieldValues}
                      interactive={false}
                    />
                  </button>
                  <div className="slide-meta">
                    <div>
                      <h3>{slide.name}</h3>
                      <span>{slide.definition.name}</span>
                      {slidePlaylistAssignments && !isOverlay ? (
                        <small className="slide-assignment">
                          Playlist:{" "}
                          {slidePlaylistAssignments[slide.id]?.name ?? "Assignment required"}
                        </small>
                      ) : null}
                    </div>
                    <span className="status-pill status-pill--good">{slide.status}</span>
                  </div>
                  <footer>
                    <span>
                      <Clock3 size={14} />{" "}
                      {slide.advanceOnVideoEnd
                        ? "Until video ends"
                        : `${slide.defaultDurationMs / 1000}s`}
                    </span>
                    <span>Updated {new Date(slide.updatedAt).toLocaleDateString()}</span>
                  </footer>
                  {playlistSlideIds && onUpdateAnimations && !isOverlay ? (
                    <div className="slide-animation-controls">
                      <label>
                        <span>Entrance</span>
                        <select
                          aria-label={`Entrance animation for ${slide.name}`}
                          value={entranceAnimation}
                          onChange={(event) =>
                            void onUpdateAnimations(slide.id, event.target.value, exitAnimation)
                          }
                        >
                          {entranceAnimationOptions.map((animation) => (
                            <option key={animation} value={animation}>
                              {animation.replaceAll("-", " ")}
                            </option>
                          ))}
                        </select>
                      </label>
                      <label>
                        <span>Ending</span>
                        <select
                          aria-label={`Ending animation for ${slide.name}`}
                          value={exitAnimation}
                          onChange={(event) =>
                            void onUpdateAnimations(slide.id, entranceAnimation, event.target.value)
                          }
                        >
                          {exitAnimationOptions.map((animation) => (
                            <option key={animation} value={animation}>
                              {animation.replaceAll("-", " ")}
                            </option>
                          ))}
                        </select>
                      </label>
                    </div>
                  ) : null}
                  <div className="card-inline-actions">
                    <button className="text-button" onClick={() => openEdit(slide)}>
                      <Pencil size={15} /> Edit
                    </button>
                    <button
                      className="text-button"
                      disabled={duplicate.isPending && duplicate.variables?.id === slide.id}
                      onClick={() => duplicateSlide(slide.id)}
                    >
                      <Copy size={15} />{" "}
                      {duplicate.isPending && duplicate.variables?.id === slide.id
                        ? "Duplicating…"
                        : "Duplicate"}
                    </button>
                    <button
                      className="text-button"
                      disabled={exportSlide.isPending && exportSlide.variables?.id === slide.id}
                      onClick={() => exportSlide.mutate(slide)}
                    >
                      <Download size={15} />{" "}
                      {exportSlide.isPending && exportSlide.variables?.id === slide.id
                        ? "Exporting…"
                        : "Export"}
                    </button>
                    {!isOverlay &&
                    onMoveToPlaylist &&
                    playlists.some(
                      (playlist) => playlist.id !== slidePlaylistAssignments?.[slide.id]?.id,
                    ) ? (
                      <button
                        className="text-button"
                        onClick={() => {
                          const targets = playlists.filter(
                            (playlist) => playlist.id !== slidePlaylistAssignments?.[slide.id]?.id,
                          );
                          setMovingSlide(slide);
                          setMoveTargetId(targets[0]?.id ?? "");
                          setMoveError(null);
                        }}
                      >
                        <FileStack size={15} /> Move
                      </button>
                    ) : null}
                    <button
                      className="text-button text-button--danger"
                      onClick={() => {
                        if (window.confirm(`Delete ${slide.name}?`)) archive.mutate(slide.id);
                      }}
                    >
                      <Trash2 size={15} /> Delete
                    </button>
                  </div>
                </article>
              );
            })}
          </div>
        </>
      ) : (
        <section className="empty-state card">
          <span className="empty-icon">
            <GalleryVerticalEnd />
          </span>
          <h2>Create your first slide</h2>
          <p>
            {playlistName
              ? `Create a slide and it will be added directly to ${playlistName}.`
              : `Create a slide and it will be assigned directly to ${creationPlaylistName ?? "the active playlist"}.`}
          </p>
          <button
            className="button button--primary"
            onClick={() => setCreating(true)}
            disabled={!playlistId}
          >
            <Plus size={17} /> Create slide
          </button>
        </section>
      )}
      <ErrorNotice error={archive.error ?? duplicate.error ?? exportSlide.error} />
      {movingSlide ? (
        <Modal
          title={`Move ${movingSlide.name}`}
          description="Moving keeps the slide intact and appends it to the selected playlist."
          onClose={() => {
            if (!movePending) setMovingSlide(null);
          }}
        >
          <form
            className="form-stack"
            onSubmit={(event) => {
              event.preventDefault();
              void moveSlide();
            }}
          >
            <label>
              <span>Destination playlist</span>
              <select
                value={moveTargetId}
                onChange={(event) => setMoveTargetId(event.target.value)}
                required
                autoFocus
              >
                {playlists
                  .filter(
                    (playlist) => playlist.id !== slidePlaylistAssignments?.[movingSlide.id]?.id,
                  )
                  .map((playlist) => (
                    <option key={playlist.id} value={playlist.id}>
                      {playlist.name}
                    </option>
                  ))}
              </select>
            </label>
            <ErrorNotice error={moveError} />
            <div className="modal-actions">
              <button
                type="button"
                className="button button--secondary"
                disabled={movePending}
                onClick={() => setMovingSlide(null)}
              >
                Cancel
              </button>
              <button
                type="submit"
                className="button button--primary"
                disabled={!moveTargetId || movePending}
              >
                {movePending ? "Moving…" : "Move slide"}
              </button>
            </div>
          </form>
        </Modal>
      ) : null}
      {preview ? (
        <Modal
          title={preview.name}
          description={`${preview.definition.name} · ${
            preview.advanceOnVideoEnd
              ? "advances when all videos finish"
              : `${preview.defaultDurationMs / 1000} seconds`
          }`}
          onClose={() => setPreview(null)}
          size="large"
        >
          <div className="large-preview">
            <ScreenPreview
              definition={preview.definition}
              values={preview.fieldValues}
              waitForVideoEnd={preview.advanceOnVideoEnd}
              videoLoop={preview.videoLoop}
              videoMuted={preview.videoMuted}
            />
          </div>
        </Modal>
      ) : null}
      {creating || editing ? (
        <Modal
          title={editing ? `Edit ${editing.name}` : "Create a slide"}
          description={
            editing
              ? "Update the content, name, and playback duration while keeping its immutable slide-type version."
              : "Choose a slide type and add its content. The slide type name is used automatically."
          }
          onClose={() => {
            setCreating(false);
            setEditing(null);
          }}
          size="large"
        >
          <form
            onSubmit={(event) =>
              void handleSubmit((input) => (editing ? update.mutate(input) : create.mutate(input)))(
                event,
              )
            }
          >
            <div className="create-slide-layout">
              <div className="form-stack">
                {editing ? (
                  <label>
                    <span>Slide name</span>
                    <input {...register("name", { required: true })} />
                  </label>
                ) : null}
                <label>
                  <span>Slide type</span>
                  <select
                    value={selectedVersionId}
                    onChange={(event) => selectType(event.target.value)}
                    required
                    disabled={Boolean(editing)}
                  >
                    <option value="">Choose a template…</option>
                    <optgroup label="Rotating slides">
                      {typesQuery.data
                        ?.filter((type) => (type.definition.purpose ?? "slide") === "slide")
                        .map((type) => (
                          <option value={type.versionId} key={type.versionId}>
                            {type.name}
                          </option>
                        ))}
                    </optgroup>
                  </select>
                </label>
                {selectedType?.definition.fields
                  .filter((field) => !field.adminOnly && field.type !== "html")
                  .map((field) => (
                    <DynamicField
                      key={field.key}
                      field={field}
                      value={values[field.key]}
                      onChange={(value) =>
                        setValues((current) => ({ ...current, [field.key]: value }))
                      }
                      assets={assetsQuery.data ?? []}
                      rssSources={rssSourcesQuery.data ?? []}
                    />
                  ))}
                <label>
                  <span>
                    {advanceOnVideoEnd
                      ? "Fallback duration if video cannot start (milliseconds)"
                      : "Default duration (milliseconds)"}
                  </span>
                  <input
                    type="number"
                    min={1000}
                    max={3600000}
                    {...register("defaultDurationMs", { valueAsNumber: true })}
                  />
                </label>
                {selectedTypeHasVideo ? (
                  <fieldset className="video-playback-options">
                    <legend>Video playback</legend>
                    <label className="checkbox-field video-end-option">
                      <input
                        type="checkbox"
                        checked={videoLoop}
                        disabled={advanceOnVideoEnd}
                        onChange={(event) => {
                          const checked = event.target.checked;
                          setValue("videoLoop", checked, { shouldDirty: true });
                          if (checked) setValue("advanceOnVideoEnd", false, { shouldDirty: true });
                        }}
                      />
                      <span className="video-end-option__copy">
                        <strong>Loop video</strong>
                        <small className="field-help">
                          Restart uploaded and YouTube videos when they reach the end.
                        </small>
                      </span>
                    </label>
                    <label className="checkbox-field video-end-option">
                      <input
                        type="checkbox"
                        checked={videoMuted}
                        onChange={(event) =>
                          setValue("videoMuted", event.target.checked, { shouldDirty: true })
                        }
                      />
                      <span className="video-end-option__copy">
                        <strong>Mute video audio</strong>
                        <small className="field-help">
                          Turn this off only when the display is expected to play sound.
                        </small>
                      </span>
                    </label>
                    <label className="checkbox-field video-end-option">
                      <input
                        type="checkbox"
                        checked={advanceOnVideoEnd}
                        onChange={(event) => {
                          const checked = event.target.checked;
                          setValue("advanceOnVideoEnd", checked, { shouldDirty: true });
                          if (checked) setValue("videoLoop", false, { shouldDirty: true });
                        }}
                      />
                      <span className="video-end-option__copy">
                        <strong>Wait for all videos to finish before advancing</strong>
                        <small className="field-help">
                          Looping is turned off. If playback cannot start, the fallback duration is
                          used.
                        </small>
                      </span>
                    </label>
                    {!videoMuted ? (
                      <p className="video-playback-warning" role="status">
                        Autoplay with sound can be blocked by the TV browser. The player first uses
                        the client configuration; if the browser rejects unmuted YouTube playback,
                        it automatically retries muted without viewer interaction. Configure the
                        TV/kiosk browser to permit sound autoplay when unattended audio is required.
                      </p>
                    ) : null}
                  </fieldset>
                ) : null}
                {formError ? <div className="form-error">{formError}</div> : null}
              </div>
              <div className="builder-preview">
                <span className="eyebrow">Player preview</span>
                {selectedType ? (
                  <ScreenPreview
                    definition={selectedType.definition}
                    values={tickerPreviewValues(values, rssSourcesQuery.data ?? [])}
                    waitForVideoEnd={advanceOnVideoEnd}
                    videoLoop={videoLoop}
                    videoMuted={videoMuted}
                  />
                ) : (
                  <div className="preview-empty">Choose a slide type to start.</div>
                )}
              </div>
            </div>
            <div className="modal-actions">
              <button
                type="button"
                className="button button--secondary"
                onClick={() => {
                  setCreating(false);
                  setEditing(null);
                }}
              >
                Cancel
              </button>
              <button
                type="submit"
                className="button button--primary"
                disabled={!selectedType || create.isPending || update.isPending}
              >
                {create.isPending || update.isPending
                  ? "Saving…"
                  : editing
                    ? "Save changes"
                    : "Create & publish"}
              </button>
            </div>
          </form>
        </Modal>
      ) : null}
    </div>
  );
}

interface PlaylistRow {
  id: string;
  name: string;
  status: string;
  updatedAt: string;
  version: number;
  shuffle: boolean;
  overlaySlideId: string | null;
  items: Array<{
    id: string;
    slideId: string;
    position: number;
    durationMs: number | null;
    entranceAnimation: string | null;
    exitAnimation: string | null;
    animationDurationMs: number | null;
    slideName: string;
  }>;
}

export function PlaylistsPage() {
  const queryClient = useQueryClient();
  const [playlistParams, setPlaylistParams] = useSearchParams();
  const [creating, setCreating] = useState(false);
  const [editing, setEditing] = useState<PlaylistRow | null>(null);
  const activePlaylistId = playlistParams.get("playlist") ?? "";
  const [renaming, setRenaming] = useState(false);
  const [renameName, setRenameName] = useState("");
  const [playlistActionError, setPlaylistActionError] = useState<Error | null>(null);
  const [name, setName] = useState("");
  const [shuffleSlides, setShuffleSlides] = useState(false);
  const [overlays, setOverlays] = useState({ overlaySlideId: "" });
  const [overlayTemplateVersionId, setOverlayTemplateVersionId] = useState("");
  const [overlayValues, setOverlayValues] = useState<SlideFieldValues>({});
  const [overlayEditorSlideId, setOverlayEditorSlideId] = useState("");
  const playlistsQuery = useQuery({
    queryKey: ["client", "playlists"],
    queryFn: () => api<PlaylistRow[]>("/api/v1/client/playlists"),
  });
  const slidesQuery = useQuery({
    queryKey: ["client", "slides"],
    queryFn: () => api<SlideRow[]>("/api/v1/client/slides"),
  });
  const slideTypesQuery = useQuery({
    queryKey: ["client", "slide-types"],
    queryFn: () => api<SlideTypeRow[]>("/api/v1/client/slide-types"),
  });
  const assetsQuery = useQuery({
    queryKey: ["client", "assets"],
    queryFn: () =>
      api<Array<{ id: string; name: string; publicUrl: string; mimeType: string }>>(
        "/api/v1/client/assets",
      ),
    enabled: Boolean(editing),
  });
  const rssSourcesQuery = useQuery({
    queryKey: ["client", "rss-sources"],
    queryFn: () => api<ClientRssSource[]>("/api/v1/client/rss-sources"),
    enabled: Boolean(editing),
  });
  const selectedOverlay = (slidesQuery.data ?? []).find(
    (slide) => slide.id === overlays.overlaySlideId,
  );
  const selectedOverlayType = (slideTypesQuery.data ?? []).find(
    (type) => type.versionId === overlayTemplateVersionId,
  );
  const selectedOverlayDefinition = selectedOverlay?.definition ?? selectedOverlayType?.definition;
  const selectedOverlayName = selectedOverlay?.name ?? selectedOverlayType?.name;
  const activePlaylist = playlistsQuery.data?.find((playlist) => playlist.id === activePlaylistId);
  function selectActivePlaylist(id: string): void {
    const nextParams = new URLSearchParams(playlistParams);
    if (id) nextParams.set("playlist", id);
    else nextParams.delete("playlist");
    setPlaylistParams(nextParams, { replace: true });
  }
  useEffect(() => {
    const playlists = playlistsQuery.data;
    if (!playlists) return;
    if (!playlists.length) {
      if (activePlaylistId) selectActivePlaylist("");
      return;
    }
    if (!playlists.some((playlist) => playlist.id === activePlaylistId))
      selectActivePlaylist(playlists[0]!.id);
  }, [activePlaylistId, playlistsQuery.data, playlistParams, setPlaylistParams]);

  function payloadFromPlaylist(
    playlist: PlaylistRow,
    options: {
      name?: string;
      slideIds?: string[];
      animationOverride?: {
        slideId: string;
        entranceAnimation: string;
        exitAnimation: string;
      };
    } = {},
  ) {
    const currentItems = [...playlist.items].sort((a, b) => a.position - b.position);
    return {
      name: options.name ?? playlist.name,
      shuffle: playlist.shuffle,
      overlaySlideId: playlist.overlaySlideId,
      items: (options.slideIds ?? currentItems.map((item) => item.slideId)).map((slideId) => {
        const existing = currentItems.find((item) => item.slideId === slideId);
        return {
          slideId,
          ...(existing?.durationMs ? { durationMs: existing.durationMs } : {}),
          ...(existing?.entranceAnimation ? { entranceAnimation: existing.entranceAnimation } : {}),
          ...(existing?.exitAnimation ? { exitAnimation: existing.exitAnimation } : {}),
          ...(existing?.animationDurationMs
            ? { animationDurationMs: existing.animationDurationMs }
            : {}),
          ...(options.animationOverride?.slideId === slideId
            ? {
                entranceAnimation: options.animationOverride.entranceAnimation,
                exitAnimation: options.animationOverride.exitAnimation,
              }
            : {}),
        };
      }),
    };
  }
  const payload = (overlaySlideId = overlays.overlaySlideId || null) => ({
    name,
    shuffle: shuffleSlides,
    items: editing
      ? [...editing.items]
          .sort((a, b) => a.position - b.position)
          .map((item) => ({
            slideId: item.slideId,
            ...(item.durationMs ? { durationMs: item.durationMs } : {}),
            ...(item.entranceAnimation ? { entranceAnimation: item.entranceAnimation } : {}),
            ...(item.exitAnimation ? { exitAnimation: item.exitAnimation } : {}),
            ...(item.animationDurationMs ? { animationDurationMs: item.animationDurationMs } : {}),
          }))
      : [],
    overlaySlideId,
  });
  const create = useMutation({
    mutationFn: () =>
      api<{ id: string }>("/api/v1/client/playlists", {
        method: "POST",
        body: JSON.stringify(payload()),
      }),
    onSuccess: async (created) => {
      await queryClient.invalidateQueries({ queryKey: ["client", "playlists"] });
      selectActivePlaylist(created.id);
      setCreating(false);
      setName("");
      setShuffleSlides(false);
    },
  });
  const update = useMutation({
    mutationFn: async () => {
      let overlaySlideId = overlays.overlaySlideId || null;
      if (selectedOverlay) {
        await api(`/api/v1/client/slides/${selectedOverlay.id}`, {
          method: "PATCH",
          body: JSON.stringify({ fieldValues: overlayValues }),
        });
      } else if (selectedOverlayType && editing) {
        const created = await api<{ id: string }>("/api/v1/client/slides", {
          method: "POST",
          body: JSON.stringify({
            playlistId: editing.id,
            slideTypeVersionId: selectedOverlayType.versionId,
            name: selectedOverlayType.name,
            fieldValues: overlayValues,
            status: "published",
            defaultDurationMs: 10_000,
            advanceOnVideoEnd: false,
            videoLoop: true,
            videoMuted: true,
          }),
        });
        overlaySlideId = created.id;
      }
      return api(`/api/v1/client/playlists/${editing?.id ?? ""}`, {
        method: "PUT",
        body: JSON.stringify(payload(overlaySlideId)),
      });
    },
    onSuccess: async () => {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ["client", "playlists"] }),
        queryClient.invalidateQueries({ queryKey: ["client", "slides"] }),
      ]);
      setEditing(null);
      setName("");
      setShuffleSlides(false);
      setOverlayTemplateVersionId("");
      setOverlayValues({});
      setOverlayEditorSlideId("");
    },
  });
  const archiveOverlay = useMutation({
    mutationFn: (id: string) => api(`/api/v1/client/slides/${id}`, { method: "DELETE" }),
    onSuccess: async () => {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ["client", "playlists"] }),
        queryClient.invalidateQueries({ queryKey: ["client", "slides"] }),
      ]);
      setOverlays({ overlaySlideId: "" });
      setOverlayTemplateVersionId("");
      setOverlayValues({});
      setOverlayEditorSlideId("");
    },
  });
  const archive = useMutation({
    mutationFn: (id: string) => api(`/api/v1/client/playlists/${id}`, { method: "DELETE" }),
    onSuccess: async (_, id) => {
      if (id === activePlaylistId) selectActivePlaylist("");
      await queryClient.invalidateQueries({ queryKey: ["client", "playlists"] });
    },
  });
  const exportPlaylist = useMutation({
    mutationFn: (playlist: PlaylistRow) =>
      api<unknown>(`/api/v1/client/playlists/${playlist.id}/export`),
    onSuccess: (document, playlist) => {
      setPlaylistActionError(null);
      downloadJsonFile(document, `${portableFilename(playlist.name)}.playlist.json`);
    },
    onError: (error) =>
      setPlaylistActionError(
        error instanceof Error ? error : new Error("The playlist could not be exported."),
      ),
  });
  const importPlaylist = useMutation({
    mutationFn: (document: unknown) =>
      api<{ playlistId: string }>("/api/v1/client/playlists/import", {
        method: "POST",
        body: JSON.stringify({ document }),
      }),
    onSuccess: async (created) => {
      setPlaylistActionError(null);
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ["client", "playlists"] }),
        queryClient.invalidateQueries({ queryKey: ["client", "slides"] }),
      ]);
      selectActivePlaylist(created.playlistId);
    },
    onError: (error) =>
      setPlaylistActionError(
        error instanceof Error ? error : new Error("The playlist could not be imported."),
      ),
  });
  async function handlePlaylistImport(file: File | undefined): Promise<void> {
    if (!file) return;
    try {
      setPlaylistActionError(null);
      importPlaylist.mutate(await readJsonFile(file));
    } catch (error) {
      setPlaylistActionError(
        error instanceof Error ? error : new Error("The file could not be read."),
      );
    }
  }
  const rename = useMutation({
    mutationFn: () => {
      if (!activePlaylist) throw new Error("Choose a playlist first.");
      return api(`/api/v1/client/playlists/${activePlaylist.id}`, {
        method: "PUT",
        body: JSON.stringify(payloadFromPlaylist(activePlaylist, { name: renameName.trim() })),
      });
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ["client", "playlists"] });
      setRenaming(false);
      setRenameName("");
    },
  });
  async function updateActivePlaylistSlides(nextSlideIds: string[]): Promise<void> {
    if (!activePlaylist) throw new Error("Choose a playlist first.");
    setPlaylistActionError(null);
    try {
      await api(`/api/v1/client/playlists/${activePlaylist.id}`, {
        method: "PUT",
        body: JSON.stringify(payloadFromPlaylist(activePlaylist, { slideIds: nextSlideIds })),
      });
      await queryClient.invalidateQueries({ queryKey: ["client", "playlists"] });
    } catch (error) {
      setPlaylistActionError(
        error instanceof Error ? error : new Error("The playlist slides could not be updated."),
      );
      throw error;
    }
  }
  async function updateActiveSlideAnimations(
    slideId: string,
    entranceAnimation: string,
    exitAnimation: string,
  ): Promise<void> {
    if (!activePlaylist) return;
    setPlaylistActionError(null);
    try {
      await api(`/api/v1/client/playlists/${activePlaylist.id}`, {
        method: "PUT",
        body: JSON.stringify(
          payloadFromPlaylist(activePlaylist, {
            animationOverride: { slideId, entranceAnimation, exitAnimation },
          }),
        ),
      });
      await queryClient.invalidateQueries({ queryKey: ["client", "playlists"] });
    } catch (error) {
      setPlaylistActionError(
        error instanceof Error ? error : new Error("The slide animations could not be saved."),
      );
      throw error;
    }
  }
  async function moveSlideToPlaylist(slideId: string, playlistId: string): Promise<void> {
    setPlaylistActionError(null);
    try {
      await api(`/api/v1/client/slides/${slideId}/playlist`, {
        method: "PATCH",
        body: JSON.stringify({ playlistId }),
      });
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ["client", "playlists"] }),
        queryClient.invalidateQueries({ queryKey: ["client", "slides"] }),
      ]);
    } catch (error) {
      setPlaylistActionError(
        error instanceof Error ? error : new Error("The slide could not be moved."),
      );
      throw error;
    }
  }
  function openPlaylist(playlist: PlaylistRow): void {
    void slidesQuery.refetch();
    const overlay = (slidesQuery.data ?? []).find((slide) => slide.id === playlist.overlaySlideId);
    setEditing(playlist);
    setName(playlist.name);
    setShuffleSlides(playlist.shuffle);
    setOverlays({ overlaySlideId: playlist.overlaySlideId ?? "" });
    setOverlayTemplateVersionId("");
    setOverlayValues(overlay?.fieldValues ?? {});
    setOverlayEditorSlideId(overlay?.id ?? "");
  }
  function startCreatePlaylist(): void {
    setEditing(null);
    setName("");
    setShuffleSlides(false);
    setOverlays({ overlaySlideId: "" });
    setOverlayTemplateVersionId("");
    setOverlayValues({});
    setOverlayEditorSlideId("");
    setCreating(true);
  }
  useEffect(() => {
    if (
      !overlays.overlaySlideId ||
      overlayTemplateVersionId ||
      overlayEditorSlideId === overlays.overlaySlideId ||
      !selectedOverlay
    )
      return;
    setOverlayValues(selectedOverlay.fieldValues);
    setOverlayEditorSlideId(selectedOverlay.id);
  }, [overlayEditorSlideId, overlayTemplateVersionId, overlays.overlaySlideId, selectedOverlay]);
  const playlistBySlideId = new Map<string, { id: string; name: string }>();
  for (const playlist of playlistsQuery.data ?? [])
    for (const item of playlist.items)
      playlistBySlideId.set(item.slideId, { id: playlist.id, name: playlist.name });
  const slidePlaylistAssignments = Object.fromEntries(playlistBySlideId.entries());
  return (
    <div className="page-stack">
      <section className="page-actions">
        <div>
          <h2>Content</h2>
          <p>
            Choose a playlist, then manage its rotating slides and reusable overlay in one place.
          </p>
        </div>
        <div className="page-action-buttons">
          <label
            className={`button button--secondary ${importPlaylist.isPending ? "is-disabled" : ""}`}
          >
            <Upload size={17} /> {importPlaylist.isPending ? "Importing…" : "Import playlist"}
            <input
              className="visually-hidden"
              type="file"
              accept="application/json,.json"
              disabled={importPlaylist.isPending}
              onChange={(event) => {
                void handlePlaylistImport(event.target.files?.[0]);
                event.currentTarget.value = "";
              }}
            />
          </label>
          <button className="button button--primary" onClick={startCreatePlaylist}>
            <Plus size={17} /> New playlist
          </button>
        </div>
      </section>
      <ErrorNotice error={playlistActionError || archive.error} />
      {playlistsQuery.isLoading ? (
        <PageLoading />
      ) : playlistsQuery.error ? (
        <ErrorNotice error={playlistsQuery.error} />
      ) : playlistsQuery.data?.length && activePlaylist ? (
        <>
          <section className="content-playlist-bar card">
            <label className="content-playlist-picker">
              <span>Active playlist</span>
              <select
                aria-label="Active playlist"
                value={activePlaylist.id}
                onChange={(event) => {
                  selectActivePlaylist(event.target.value);
                  setPlaylistActionError(null);
                }}
              >
                {playlistsQuery.data.map((playlist) => (
                  <option key={playlist.id} value={playlist.id}>
                    {playlist.name} · {playlist.items.length} slide
                    {playlist.items.length === 1 ? "" : "s"}
                  </option>
                ))}
              </select>
            </label>
            <div className="content-playlist-summary" aria-live="polite">
              <span className="status-pill status-pill--good">{activePlaylist.status}</span>
              <span>
                {activePlaylist.items.length} slide
                {activePlaylist.items.length === 1 ? "" : "s"}
              </span>
              <span>{activePlaylist.shuffle ? "Shuffle on" : "Ordered playback"}</span>
              <span>{activePlaylist.overlaySlideId ? "Overlay assigned" : "No overlay"}</span>
            </div>
            <div className="content-playlist-actions">
              <button
                className="button button--secondary"
                onClick={() => {
                  setRenameName(activePlaylist.name);
                  setRenaming(true);
                }}
              >
                <Pencil size={16} /> Rename
              </button>
              <button
                className="button button--secondary"
                onClick={() => openPlaylist(activePlaylist)}
              >
                <Settings size={16} /> Playlist settings
              </button>
              <button
                className="button button--secondary"
                disabled={
                  exportPlaylist.isPending && exportPlaylist.variables?.id === activePlaylist.id
                }
                onClick={() => exportPlaylist.mutate(activePlaylist)}
              >
                <Download size={16} />{" "}
                {exportPlaylist.isPending && exportPlaylist.variables?.id === activePlaylist.id
                  ? "Exporting…"
                  : "Export playlist"}
              </button>
              <button
                className="button button--danger"
                disabled={activePlaylist.items.length > 0 || Boolean(activePlaylist.overlaySlideId)}
                title={
                  activePlaylist.items.length || activePlaylist.overlaySlideId
                    ? "Delete every rotating and overlay slide before deleting this playlist."
                    : "Delete this empty playlist"
                }
                onClick={() => {
                  if (window.confirm(`Delete ${activePlaylist.name}?`))
                    archive.mutate(activePlaylist.id);
                }}
              >
                <Trash2 size={16} /> Delete playlist
              </button>
            </div>
          </section>
          <ClientSlidesPage
            embedded
            playlistId={activePlaylist.id}
            creationPlaylistName={activePlaylist.name}
            playlistName={activePlaylist.name}
            playlistSlideIds={[...activePlaylist.items]
              .sort((a, b) => a.position - b.position)
              .map((item) => item.slideId)}
            slidePlaylistAssignments={slidePlaylistAssignments}
            playlistItemSettings={Object.fromEntries(
              activePlaylist.items.map((item) => [
                item.slideId,
                {
                  entranceAnimation: item.entranceAnimation,
                  exitAnimation: item.exitAnimation,
                },
              ]),
            )}
            playlists={playlistsQuery.data.map((playlist) => ({
              id: playlist.id,
              name: playlist.name,
            }))}
            onMoveToPlaylist={moveSlideToPlaylist}
            onReorderSlides={updateActivePlaylistSlides}
            onUpdateAnimations={updateActiveSlideAnimations}
          />
        </>
      ) : (
        <section className="empty-state card">
          <span className="empty-icon">
            <FileStack />
          </span>
          <h2>Create your first playlist</h2>
          <p>Create a playlist, then create its slides directly from this page.</p>
          <button className="button button--primary" onClick={startCreatePlaylist}>
            <Plus size={17} /> New playlist
          </button>
        </section>
      )}
      {renaming && activePlaylist ? (
        <Modal
          title={`Rename ${activePlaylist.name}`}
          description="This updates the playlist name everywhere it is shown. Assigned televisions keep playing it."
          onClose={() => setRenaming(false)}
        >
          <form
            className="form-stack"
            onSubmit={(event) => {
              event.preventDefault();
              rename.mutate();
            }}
          >
            <label>
              <span>Playlist name</span>
              <input
                value={renameName}
                onChange={(event) => setRenameName(event.target.value)}
                required
                autoFocus
              />
            </label>
            <ErrorNotice error={rename.error} />
            <div className="modal-actions">
              <button
                type="button"
                className="button button--secondary"
                onClick={() => setRenaming(false)}
              >
                Cancel
              </button>
              <button
                type="submit"
                className="button button--primary"
                disabled={!renameName.trim() || rename.isPending}
              >
                {rename.isPending ? "Renaming…" : "Rename playlist"}
              </button>
            </div>
          </form>
        </Modal>
      ) : null}
      {creating || editing ? (
        <Modal
          title={editing ? `Edit ${editing.name}` : "Create a playlist"}
          description="Choose the playlist-wide overlay and build ticker or logo boxes inside its template. Arrange and move slides directly on the Content page."
          size="workspace"
          onClose={() => {
            setCreating(false);
            setEditing(null);
            setShuffleSlides(false);
            setOverlayTemplateVersionId("");
            setOverlayValues({});
            setOverlayEditorSlideId("");
          }}
        >
          <form
            className="form-stack playlist-settings-form"
            onSubmit={(event) => {
              event.preventDefault();
              if (editing) update.mutate();
              else create.mutate();
            }}
          >
            <label>
              <span>Playlist name</span>
              <input
                value={name}
                onChange={(event) => setName(event.target.value)}
                required
                placeholder="Main lobby rotation"
              />
            </label>
            <fieldset className="playlist-settings-section">
              <legend>Slide playback</legend>
              <label className="toggle-row">
                <input
                  type="checkbox"
                  checked={shuffleSlides}
                  onChange={(event) => setShuffleSlides(event.target.checked)}
                />
                <span>
                  <strong>Shuffle slides</strong>
                  <small>
                    Each player starts this playlist in a randomized order. Dashboard drag order is
                    preserved for editing and for when shuffle is turned off.
                  </small>
                </span>
              </label>
            </fieldset>
            <fieldset className="playlist-settings-section">
              <legend>Playlist overlay</legend>
              <p className="field-help">
                An overlay is an admin-defined overlay slide rendered above every rotating slide.
                Add news ticker and logo elements in its template, then edit their content like any
                other slide.
              </p>
              <label>
                <span>Active overlay slide</span>
                <select
                  value={
                    overlayTemplateVersionId
                      ? `template:${overlayTemplateVersionId}`
                      : overlays.overlaySlideId
                        ? `slide:${overlays.overlaySlideId}`
                        : ""
                  }
                  disabled={!editing}
                  onChange={(event) => {
                    const [kind, id = ""] = event.target.value.split(":");
                    const overlay =
                      kind === "slide"
                        ? (slidesQuery.data ?? []).find((slide) => slide.id === id)
                        : undefined;
                    const template =
                      kind === "template"
                        ? (slideTypesQuery.data ?? []).find((type) => type.versionId === id)
                        : undefined;
                    setOverlays((current) => ({
                      ...current,
                      overlaySlideId: overlay?.id ?? "",
                    }));
                    setOverlayTemplateVersionId(template?.versionId ?? "");
                    setOverlayValues(
                      overlay?.fieldValues ??
                        (template ? sampleValues(template.definition, true) : {}),
                    );
                    setOverlayEditorSlideId(overlay?.id ?? "");
                  }}
                >
                  <option value="">No overlay</option>
                  <optgroup label="Existing overlay slides">
                    {(slidesQuery.data ?? [])
                      .filter(
                        (slide) =>
                          slide.playlistId === editing?.id &&
                          (slide.definition.purpose ?? "slide") === "overlay",
                      )
                      .map((slide) => (
                        <option
                          key={slide.id}
                          value={`slide:${slide.id}`}
                          disabled={slide.status !== "published"}
                        >
                          {slide.name}
                          {slide.status !== "published" ? " (publish it first)" : ""}
                        </option>
                      ))}
                  </optgroup>
                  <optgroup label="Published overlay templates">
                    {(slideTypesQuery.data ?? [])
                      .filter((type) => type.definition.purpose === "overlay")
                      .map((type) => (
                        <option key={type.versionId} value={`template:${type.versionId}`}>
                          {type.name}
                        </option>
                      ))}
                  </optgroup>
                </select>
              </label>
              {editing && (slidesQuery.isFetching || slideTypesQuery.isFetching) ? (
                <small className="field-help">Refreshing available overlays…</small>
              ) : editing &&
                !(slideTypesQuery.data ?? []).some(
                  (type) => type.definition.purpose === "overlay",
                ) &&
                !(slidesQuery.data ?? []).some(
                  (slide) =>
                    slide.playlistId === editing.id &&
                    (slide.definition.purpose ?? "slide") === "overlay",
                ) ? (
                <small className="field-help">
                  No published overlay template is available to this client.
                </small>
              ) : null}
              {selectedOverlayDefinition && selectedOverlayName ? (
                <section className="overlay-content-editor" aria-labelledby="overlay-content-title">
                  <div className="overlay-content-editor__heading">
                    <div>
                      <h3 id="overlay-content-title">{selectedOverlayName} content</h3>
                      <p className="field-help">
                        Complete the fields required by this overlay template. These values are
                        saved as a published overlay slide and appear above every rotating slide.
                      </p>
                    </div>
                    <div className="overlay-content-editor__actions">
                      <span className="status-pill status-pill--good">Selected overlay</span>
                      {selectedOverlay ? (
                        <button
                          type="button"
                          className="text-button text-button--danger"
                          disabled={archiveOverlay.isPending}
                          onClick={() => {
                            if (window.confirm(`Delete ${selectedOverlay.name}?`))
                              archiveOverlay.mutate(selectedOverlay.id);
                          }}
                        >
                          <Trash2 size={15} />
                          {archiveOverlay.isPending ? "Deleting…" : "Delete overlay"}
                        </button>
                      ) : null}
                    </div>
                  </div>
                  <div className="overlay-content-editor__layout">
                    <div className="form-stack overlay-content-editor__fields">
                      {selectedOverlayDefinition.fields
                        .filter((field) => !field.adminOnly && field.type !== "html")
                        .map((field) => (
                          <DynamicField
                            key={field.key}
                            field={field}
                            value={overlayValues[field.key]}
                            onChange={(value) =>
                              setOverlayValues((current) => ({
                                ...current,
                                [field.key]: value,
                              }))
                            }
                            assets={assetsQuery.data ?? []}
                            rssSources={rssSourcesQuery.data ?? []}
                          />
                        ))}
                      {assetsQuery.error ? <ErrorNotice error={assetsQuery.error} /> : null}
                      {rssSourcesQuery.error ? <ErrorNotice error={rssSourcesQuery.error} /> : null}
                    </div>
                    <div className="builder-preview overlay-content-editor__preview">
                      <span className="eyebrow">Overlay preview</span>
                      <ScreenPreview
                        definition={selectedOverlayDefinition}
                        values={tickerPreviewValues(overlayValues, rssSourcesQuery.data ?? [])}
                        ariaLabel={`${selectedOverlayName} overlay preview`}
                      />
                    </div>
                  </div>
                </section>
              ) : null}
              {!editing ? (
                <small className="field-help">
                  Create the playlist first, then create an overlay slide and assign it here.
                </small>
              ) : null}
            </fieldset>
            <ErrorNotice error={create.error ?? update.error ?? archiveOverlay.error} />
            <div className="modal-actions">
              <button
                type="button"
                className="button button--secondary"
                onClick={() => {
                  setCreating(false);
                  setEditing(null);
                  setShuffleSlides(false);
                  setOverlayTemplateVersionId("");
                  setOverlayValues({});
                  setOverlayEditorSlideId("");
                }}
              >
                Cancel
              </button>
              <button
                type="submit"
                className="button button--primary"
                disabled={!name || create.isPending || update.isPending}
              >
                {create.isPending || update.isPending
                  ? "Saving…"
                  : editing
                    ? selectedOverlayDefinition
                      ? "Save overlay & playlist"
                      : "Save playlist"
                    : "Create playlist"}
              </button>
            </div>
          </form>
        </Modal>
      ) : null}
    </div>
  );
}

interface TvRow {
  id: string;
  name: string;
  slug: string;
  status: string;
  connectionStatus?: string;
  orientation: string;
  resolutionWidth: number;
  resolutionHeight: number;
  zoomPercent: number;
  backgroundColor: string;
  backgroundAssetId?: string | null;
  timezone: string;
  lastSeenAt?: string;
  activePlaylistId?: string;
  audioLibraryId?: string;
  audioAssetIds: string[];
  audioShuffle: boolean;
  audioMuted: boolean;
  controllerEnabled: boolean;
  audioControllerEnabled: boolean;
  controllerCount?: number;
  activated?: boolean;
  activation?: { code: string; expiresAt: string };
}

interface AudioLibraryRow {
  id: string;
  name: string;
  controllerEnabled: boolean;
  items: Array<{ assetId: string; assetName: string; position: number }>;
}

export function TvsPage({ mode, playerUrl }: { mode: AccessMode; playerUrl: string | null }) {
  const queryClient = useQueryClient();
  const [creating, setCreating] = useState(false);
  const [editingTv, setEditingTv] = useState<TvRow | null>(null);
  const [activation, setActivation] = useState<{ code: string } | null>(null);
  const [controllerClaimTv, setControllerClaimTv] = useState<TvRow | null>(null);
  const [controllerPin, setControllerPin] = useState("");
  const [audioAssetToAdd, setAudioAssetToAdd] = useState("");
  const [draggedAudioAssetId, setDraggedAudioAssetId] = useState("");
  const [form, setForm] = useState({
    name: "",
    timezone: "Asia/Qatar",
    orientation: "landscape",
    resolutionWidth: 1920,
    resolutionHeight: 1080,
    zoomPercent: 100,
    backgroundColor: "#000000",
    backgroundAssetId: "",
    activePlaylistId: "",
    audioLibraryId: "",
    audioAssetIds: [] as string[],
    audioShuffle: false,
    audioMuted: false,
    controllerEnabled: false,
    audioControllerEnabled: false,
  });
  const endpoint = mode === "admin" ? "/api/v1/admin/tvs" : "/api/v1/client/tvs";
  const query = useQuery({
    queryKey: [mode, "tvs"],
    queryFn: () => api<TvRow[]>(endpoint),
    refetchInterval: mode === "client" ? 60_000 : false,
  });
  const playlistsQuery = useQuery({
    queryKey: ["client", "playlists"],
    queryFn: () => api<PlaylistRow[]>("/api/v1/client/playlists"),
    enabled: mode === "client",
  });
  const assetsQuery = useQuery({
    queryKey: ["client", "assets"],
    queryFn: () =>
      api<Array<{ id: string; name: string; mimeType: string }>>("/api/v1/client/assets"),
    enabled: mode === "client",
  });
  const audioLibrariesQuery = useQuery({
    queryKey: ["client", "audio-libraries"],
    queryFn: () => api<AudioLibraryRow[]>("/api/v1/client/audio-libraries"),
    enabled: mode === "client",
  });
  const create = useMutation({
    mutationFn: () =>
      api<{ tv: TvRow; activation: { code: string } }>("/api/v1/client/tvs", {
        method: "POST",
        body: JSON.stringify({
          name: form.name,
          timezone: form.timezone,
          orientation: form.orientation,
          resolutionWidth: form.resolutionWidth,
          resolutionHeight: form.resolutionHeight,
          zoomPercent: form.zoomPercent,
          backgroundColor: form.backgroundColor,
          backgroundAssetId: form.backgroundAssetId || null,
          activePlaylistId: form.activePlaylistId || null,
          audioLibraryId: form.audioLibraryId || null,
          audioAssetIds: form.audioAssetIds,
          audioShuffle: form.audioShuffle,
          audioMuted: form.audioMuted,
          controllerEnabled: form.controllerEnabled,
          audioControllerEnabled: form.audioControllerEnabled,
        }),
      }),
    onSuccess: async (data) => {
      await queryClient.invalidateQueries({ queryKey: ["client", "tvs"] });
      await queryClient.invalidateQueries({ queryKey: ["client", "overview"] });
      setCreating(false);
      setAudioAssetToAdd("");
      setActivation({ code: data.activation.code });
    },
  });
  const update = useMutation({
    mutationFn: () =>
      api(`/api/v1/client/tvs/${editingTv?.id ?? ""}`, {
        method: "PATCH",
        body: JSON.stringify({
          ...form,
          backgroundAssetId: form.backgroundAssetId || null,
          activePlaylistId: form.activePlaylistId || null,
          audioLibraryId: form.audioLibraryId || null,
        }),
      }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ["client", "tvs"] });
      setEditingTv(null);
      setAudioAssetToAdd("");
    },
  });
  const archive = useMutation({
    mutationFn: (id: string) => api(`/api/v1/client/tvs/${id}`, { method: "DELETE" }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ["client", "tvs"] });
      await queryClient.invalidateQueries({ queryKey: ["client", "overview"] });
    },
  });
  const claimController = useMutation({
    mutationFn: () =>
      api(`/api/v1/client/tvs/${controllerClaimTv?.id ?? ""}/controller/claim`, {
        method: "POST",
        body: JSON.stringify({ pairingCode: controllerPin }),
      }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ["client", "tvs"] });
      setControllerClaimTv(null);
      setControllerPin("");
    },
  });
  const revokeControllers = useMutation({
    mutationFn: (id: string) =>
      api(`/api/v1/client/tvs/${id}/controller/revoke`, { method: "POST" }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["client", "tvs"] }),
  });
  const refreshTv = useMutation({
    mutationFn: (id: string) =>
      api<{ id: string; requestedAt: string; expectedWithinMs: number }>(
        `/api/v1/client/tvs/${id}/refresh`,
        { method: "POST" },
      ),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ["client", "tvs"] });
    },
  });
  function openTvEditor(tv: TvRow): void {
    setEditingTv(tv);
    setForm({
      name: tv.name,
      timezone: tv.timezone,
      orientation: tv.orientation,
      resolutionWidth: tv.resolutionWidth,
      resolutionHeight: tv.resolutionHeight,
      zoomPercent: tv.zoomPercent,
      backgroundColor: tv.backgroundColor,
      backgroundAssetId: tv.backgroundAssetId ?? "",
      activePlaylistId: tv.activePlaylistId ?? "",
      audioLibraryId: tv.audioLibraryId ?? "",
      audioAssetIds: tv.audioAssetIds,
      audioShuffle: tv.audioShuffle,
      audioMuted: tv.audioMuted,
      controllerEnabled: tv.controllerEnabled,
      audioControllerEnabled: tv.audioControllerEnabled,
    });
    setAudioAssetToAdd("");
  }
  function startCreateTv(): void {
    setForm({
      name: "",
      timezone: "Asia/Qatar",
      orientation: "landscape",
      resolutionWidth: 1920,
      resolutionHeight: 1080,
      zoomPercent: 100,
      backgroundColor: "#000000",
      backgroundAssetId: "",
      activePlaylistId: "",
      audioLibraryId: "",
      audioAssetIds: [],
      audioShuffle: false,
      audioMuted: false,
      controllerEnabled: false,
      audioControllerEnabled: false,
    });
    setAudioAssetToAdd("");
    setCreating(true);
  }
  const mp3Assets = (assetsQuery.data ?? []).filter((asset) => asset.mimeType === "audio/mpeg");
  const imageAssets = (assetsQuery.data ?? []).filter((asset) =>
    asset.mimeType.startsWith("image/"),
  );
  const publicPlayerUrl = playerUrl || "/player";
  function moveAudioAsset(sourceId: string, targetId: string, placement: "before" | "after"): void {
    if (sourceId === targetId) return;
    setForm((current) => {
      const next = current.audioAssetIds.filter((id) => id !== sourceId);
      const targetIndex = next.indexOf(targetId);
      if (targetIndex < 0) return current;
      next.splice(targetIndex + (placement === "after" ? 1 : 0), 0, sourceId);
      return { ...current, audioAssetIds: next };
    });
  }
  function moveAudioAssetByKeyboard(assetId: string, direction: -1 | 1): void {
    setForm((current) => {
      const index = current.audioAssetIds.indexOf(assetId);
      const target = index + direction;
      if (index < 0 || target < 0 || target >= current.audioAssetIds.length) return current;
      const next = [...current.audioAssetIds];
      [next[index], next[target]] = [next[target]!, next[index]!];
      return { ...current, audioAssetIds: next };
    });
  }
  function audioSettings(): ReactNode {
    const availableAssets = mp3Assets.filter((asset) => !form.audioAssetIds.includes(asset.id));
    const selectedLibrary = audioLibrariesQuery.data?.find(
      (library) => library.id === form.audioLibraryId,
    );
    return (
      <fieldset className="tv-audio-settings">
        <legend>
          <Music2 size={17} /> TV audio playlist
        </legend>
        <p>
          Audio tracks autoplay in a continuous loop behind the slides. Choose an administrator list
          or manage a custom list for this TV.
        </p>
        <label className="checkbox-field">
          <input
            type="checkbox"
            checked={form.controllerEnabled}
            onChange={(event) => setForm({ ...form, controllerEnabled: event.target.checked })}
          />
          <span>
            <Gamepad2 size={16} />
            <strong>Enable TV controller</strong>
            <small>Master access switch for this television.</small>
          </span>
        </label>
        <button
          type="button"
          className={`button ${form.audioControllerEnabled ? "button--primary" : "button--secondary"}`}
          onClick={() => setForm({ ...form, audioControllerEnabled: !form.audioControllerEnabled })}
        >
          <Gamepad2 size={16} />{" "}
          {form.audioControllerEnabled ? "Added To Controller" : "Add To Controller"}
        </button>
        <label>
          <span>Audio source</span>
          <select
            value={form.audioLibraryId}
            onChange={(event) => setForm({ ...form, audioLibraryId: event.target.value })}
          >
            <option value="">Custom tracks for this TV</option>
            {audioLibrariesQuery.data?.map((library) => (
              <option value={library.id} key={library.id}>
                {library.name} · {library.items.length} track
                {library.items.length === 1 ? "" : "s"}
              </option>
            ))}
          </select>
        </label>
        <div className="tv-audio-options">
          <label className="checkbox-field">
            <input
              type="checkbox"
              checked={form.audioShuffle}
              onChange={(event) => setForm({ ...form, audioShuffle: event.target.checked })}
            />
            <span>
              <Shuffle size={16} />
              <strong>Shuffle tracks</strong>
              <small>Randomize the order again after each completed loop.</small>
            </span>
          </label>
          <label className="checkbox-field">
            <input
              type="checkbox"
              checked={form.audioMuted}
              onChange={(event) => setForm({ ...form, audioMuted: event.target.checked })}
            />
            <span>
              <VolumeX size={16} />
              <strong>Mute TV audio</strong>
              <small>Keep the playlist configured but silence it on this TV.</small>
            </span>
          </label>
        </div>
        {selectedLibrary ? (
          <div className="tv-audio-library-summary">
            <strong>{selectedLibrary.name}</strong>
            <ol>
              {selectedLibrary.items.map((item) => (
                <li key={item.assetId}>{item.assetName}</li>
              ))}
            </ol>
            <small>
              The platform administrator controls this list. Updates automatically reach this TV.
            </small>
          </div>
        ) : (
          <>
            <div className="tv-audio-picker">
              <label>
                <span>Add MP3 track</span>
                <select
                  value={audioAssetToAdd}
                  onChange={(event) => setAudioAssetToAdd(event.target.value)}
                  disabled={!availableAssets.length}
                >
                  <option value="">
                    {availableAssets.length
                      ? "Choose an uploaded MP3"
                      : "No more MP3 tracks available"}
                  </option>
                  {availableAssets.map((asset) => (
                    <option value={asset.id} key={asset.id}>
                      {asset.name}
                    </option>
                  ))}
                </select>
              </label>
              <button
                type="button"
                className="button button--secondary"
                disabled={!audioAssetToAdd}
                onClick={() => {
                  if (!audioAssetToAdd) return;
                  setForm((current) => ({
                    ...current,
                    audioAssetIds: [...current.audioAssetIds, audioAssetToAdd],
                  }));
                  setAudioAssetToAdd("");
                }}
              >
                <Plus size={16} /> Add track
              </button>
            </div>
            {form.audioAssetIds.length ? (
              <ol className="tv-audio-list" aria-label="TV audio play order">
                {form.audioAssetIds.map((assetId, index) => {
                  const asset = mp3Assets.find((candidate) => candidate.id === assetId);
                  return (
                    <li
                      key={assetId}
                      className={draggedAudioAssetId === assetId ? "is-dragging" : ""}
                      onDragOver={(event) => {
                        if (!draggedAudioAssetId || draggedAudioAssetId === assetId) return;
                        event.preventDefault();
                        event.dataTransfer.dropEffect = "move";
                      }}
                      onDrop={(event) => {
                        event.preventDefault();
                        const sourceId =
                          event.dataTransfer.getData("text/plain") || draggedAudioAssetId;
                        const bounds = event.currentTarget.getBoundingClientRect();
                        moveAudioAsset(
                          sourceId,
                          assetId,
                          event.clientY < bounds.top + bounds.height / 2 ? "before" : "after",
                        );
                        setDraggedAudioAssetId("");
                      }}
                    >
                      <span className="tv-audio-position">{index + 1}</span>
                      <span
                        className="drag-handle"
                        draggable
                        tabIndex={0}
                        role="slider"
                        aria-label={`Reorder ${asset?.name ?? "MP3 track"}. Use drag and drop or the arrow keys.`}
                        aria-valuemin={1}
                        aria-valuemax={form.audioAssetIds.length}
                        aria-valuenow={index + 1}
                        onDragStart={(event) => {
                          setDraggedAudioAssetId(assetId);
                          event.dataTransfer.effectAllowed = "move";
                          event.dataTransfer.setData("text/plain", assetId);
                        }}
                        onDragEnd={() => setDraggedAudioAssetId("")}
                        onKeyDown={(event) => {
                          if (event.key !== "ArrowUp" && event.key !== "ArrowDown") return;
                          event.preventDefault();
                          moveAudioAssetByKeyboard(assetId, event.key === "ArrowUp" ? -1 : 1);
                        }}
                      >
                        <GripVertical size={17} />
                        Drag
                      </span>
                      <span className="tv-audio-name">
                        <Music2 size={16} />
                        <strong>{asset?.name ?? "Unavailable MP3 asset"}</strong>
                      </span>
                      <button
                        type="button"
                        className="text-button text-button--danger"
                        onClick={() =>
                          setForm((current) => ({
                            ...current,
                            audioAssetIds: current.audioAssetIds.filter((id) => id !== assetId),
                          }))
                        }
                      >
                        <Trash2 size={15} /> Remove
                      </button>
                    </li>
                  );
                })}
              </ol>
            ) : (
              <div className="tv-audio-empty">
                <Music2 size={20} />
                <span>
                  No audio assigned. Upload MP3 files in <a href="/client/assets">Assets</a>, then
                  add them here.
                </span>
              </div>
            )}
          </>
        )}
      </fieldset>
    );
  }
  function backgroundSettings(): ReactNode {
    return (
      <fieldset className="tv-audio-settings">
        <legend>
          <FileImage size={17} /> TV transition background
        </legend>
        <p>
          This background stays behind the slide and is revealed during fade-in, fade-out, and other
          transparent transitions.
        </p>
        <div className="form-grid">
          <label>
            <span>Background color</span>
            <ColorPicker
              value={form.backgroundColor}
              onChange={(backgroundColor) => setForm({ ...form, backgroundColor })}
              allowTransparent={false}
            />
          </label>
          <label>
            <span>Background image</span>
            <select
              value={form.backgroundAssetId}
              onChange={(event) => setForm({ ...form, backgroundAssetId: event.target.value })}
            >
              <option value="">Use background color only</option>
              {imageAssets.map((asset) => (
                <option key={asset.id} value={asset.id}>
                  {asset.name}
                </option>
              ))}
            </select>
            <small>The selected image fills the TV behind all rotating slides.</small>
          </label>
        </div>
      </fieldset>
    );
  }
  function zoomSettings(id: string): ReactNode {
    const labelId = `${id}-display-zoom-label`;
    return (
      <div className="tv-zoom-field">
        <span id={labelId}>Display zoom</span>
        <div className="tv-zoom-control" role="group" aria-labelledby={labelId}>
          <button
            type="button"
            onClick={() =>
              setForm((current) => ({
                ...current,
                zoomPercent: stepTvZoom(current.zoomPercent, -1),
              }))
            }
            disabled={form.zoomPercent <= TV_ZOOM_MIN}
            aria-label={`Decrease display zoom by ${TV_ZOOM_STEP}%`}
          >
            <Minus size={16} aria-hidden="true" />
          </button>
          <output aria-live="polite" aria-atomic="true">
            {form.zoomPercent}%
          </output>
          <button
            type="button"
            onClick={() =>
              setForm((current) => ({
                ...current,
                zoomPercent: stepTvZoom(current.zoomPercent, 1),
              }))
            }
            disabled={form.zoomPercent >= TV_ZOOM_MAX}
            aria-label={`Increase display zoom by ${TV_ZOOM_STEP}%`}
          >
            <Plus size={16} aria-hidden="true" />
          </button>
        </div>
        <small>
          Works like browser zoom. Each − or + changes the logical TV view by {TV_ZOOM_STEP}%.
        </small>
      </div>
    );
  }
  return (
    <div className="page-stack">
      <section className="page-actions">
        <div>
          <h2>{mode === "admin" ? "All managed televisions" : "Televisions & activation"}</h2>
          <p>Track connectivity, player state, orientation, and secure pairing.</p>
        </div>
        {mode === "client" ? (
          <button className="button button--primary" onClick={startCreateTv}>
            <Plus size={17} /> Add television
          </button>
        ) : null}
      </section>
      {query.isLoading ? (
        <PageLoading />
      ) : query.error ? (
        <ErrorNotice error={query.error} />
      ) : (
        <div className="tv-grid">
          {query.data?.map((tv) => (
            <article className="tv-card" key={tv.id}>
              <div className="tv-screen">
                <MonitorPlay size={36} />
                <span
                  className={`online-indicator ${tv.connectionStatus === "online" ? "is-online" : ""}`}
                />
              </div>
              <div className="tv-copy">
                <div>
                  <h3>{tv.name}</h3>
                  <span
                    className={`status-pill status-pill--${tv.connectionStatus === "online" ? "good" : "neutral"}`}
                  >
                    {tv.connectionStatus ?? tv.status}
                  </span>
                </div>
                <p>{tv.activated ? "Activated display" : "Waiting for activation"}</p>
                <dl>
                  <div>
                    <dt>Orientation</dt>
                    <dd>{tv.orientation}</dd>
                  </div>
                  <div>
                    <dt>Display zoom</dt>
                    <dd>{tv.zoomPercent}%</dd>
                  </div>
                  <div>
                    <dt>Configured canvas</dt>
                    <dd>
                      {tv.resolutionWidth} × {tv.resolutionHeight}
                    </dd>
                  </div>
                  <div>
                    <dt>Timezone</dt>
                    <dd>{tv.timezone}</dd>
                  </div>
                  <div>
                    <dt>Last heartbeat</dt>
                    <dd>{tv.lastSeenAt ? new Date(tv.lastSeenAt).toLocaleString() : "Never"}</dd>
                  </div>
                  {mode === "client" ? (
                    <div>
                      <dt>Playlist</dt>
                      <dd>
                        {playlistsQuery.data?.find(
                          (playlist) => playlist.id === tv.activePlaylistId,
                        )?.name ?? "Not assigned"}
                      </dd>
                    </div>
                  ) : null}
                  {mode === "client" ? (
                    <div>
                      <dt>Audio</dt>
                      <dd>
                        {tv.audioLibraryId
                          ? `${audioLibrariesQuery.data?.find((library) => library.id === tv.audioLibraryId)?.name ?? "Predefined list"}${tv.audioMuted ? " · muted" : tv.audioShuffle ? " · shuffle" : ""}`
                          : tv.audioAssetIds.length
                            ? `${tv.audioAssetIds.length} track${tv.audioAssetIds.length === 1 ? "" : "s"}${tv.audioMuted ? " · muted" : tv.audioShuffle ? " · shuffle" : ""}`
                            : "Not assigned"}
                      </dd>
                    </div>
                  ) : null}
                </dl>
                {mode === "client" && tv.activation ? (
                  <div className="tv-pairing-code">
                    <div>
                      <span>Rotating connection code</span>
                      <strong
                        aria-label={`Connection code ${tv.activation.code.split("").join(" ")}`}
                      >
                        {tv.activation.code}
                      </strong>
                    </div>
                    <button
                      type="button"
                      className="button button--secondary"
                      onClick={() => void navigator.clipboard.writeText(tv.activation!.code)}
                    >
                      Copy
                    </button>
                    <small>
                      Rotates {new Date(tv.activation.expiresAt).toLocaleTimeString()}. A fresh code
                      remains available here after use or expiry.
                    </small>
                  </div>
                ) : null}
              </div>
              <footer>
                {mode === "client" ? (
                  <div className="card-inline-actions">
                    <a
                      className="text-button"
                      href={publicPlayerUrl}
                      target="_blank"
                      rel="noreferrer"
                    >
                      Open player <ArrowRight size={15} />
                    </a>
                    <button className="text-button" onClick={() => openTvEditor(tv)}>
                      <Pencil size={15} /> Edit
                    </button>
                    <button
                      type="button"
                      className="text-button"
                      onClick={() => {
                        setControllerClaimTv(tv);
                        setControllerPin("");
                      }}
                    >
                      <Gamepad2 size={15} /> Claim Controller
                    </button>
                    {tv.controllerCount ? (
                      <button
                        type="button"
                        className="text-button text-button--danger"
                        onClick={() => {
                          if (
                            window.confirm(
                              `Revoke ${tv.controllerCount} controller${tv.controllerCount === 1 ? "" : "s"} from ${tv.name}?`,
                            )
                          )
                            revokeControllers.mutate(tv.id);
                        }}
                      >
                        Revoke Controller
                      </button>
                    ) : null}
                    <button
                      type="button"
                      className="text-button"
                      disabled={refreshTv.isPending && refreshTv.variables === tv.id}
                      onClick={() => refreshTv.mutate(tv.id)}
                    >
                      <RefreshCw
                        size={15}
                        className={
                          refreshTv.isPending && refreshTv.variables === tv.id ? "spin" : undefined
                        }
                      />{" "}
                      {refreshTv.isPending && refreshTv.variables === tv.id
                        ? "Refreshing…"
                        : "Refresh TV"}
                    </button>
                    <button
                      className="text-button text-button--danger"
                      onClick={() => {
                        if (window.confirm(`Delete ${tv.name}?`)) archive.mutate(tv.id);
                      }}
                    >
                      <Trash2 size={15} /> Delete
                    </button>
                  </div>
                ) : (
                  <span className="muted">Customer-owned activation</span>
                )}
              </footer>
            </article>
          ))}
        </div>
      )}
      <ErrorNotice error={archive.error} />
      <ErrorNotice error={refreshTv.error} />
      <ErrorNotice error={revokeControllers.error} />
      {controllerClaimTv ? (
        <Modal
          title="Claim Controller"
          description={`Assign the controller PIN to ${controllerClaimTv.name}. The assignment remains until revoked.`}
          onClose={() => setControllerClaimTv(null)}
        >
          <form
            className="form-stack"
            onSubmit={(event) => {
              event.preventDefault();
              claimController.mutate();
            }}
          >
            <label>
              <span>Six-digit controller PIN</span>
              <input
                value={controllerPin}
                onChange={(event) =>
                  setControllerPin(event.target.value.replace(/\D/g, "").slice(0, 6))
                }
                inputMode="numeric"
                pattern="[0-9]{6}"
                required
                autoFocus
              />
            </label>
            <ErrorNotice error={claimController.error} />
            <div className="modal-actions">
              <button
                type="button"
                className="button button--secondary"
                onClick={() => setControllerClaimTv(null)}
              >
                Cancel
              </button>
              <button
                type="submit"
                className="button button--primary"
                disabled={controllerPin.length !== 6 || claimController.isPending}
              >
                Claim Controller
              </button>
            </div>
          </form>
        </Modal>
      ) : null}
      {refreshTv.isSuccess ? (
        <div className="notice" role="status">
          Refresh requested. The online TV should load the latest data within{" "}
          {Math.ceil(refreshTv.data.expectedWithinMs / 1000)} seconds.
        </div>
      ) : null}
      {creating ? (
        <Modal
          title="Add a television"
          description="Configure the display, its content playlist, and its autoplaying audio. A short-lived activation code is generated after creation."
          onClose={() => setCreating(false)}
          size="large"
        >
          <form
            className="form-stack tv-settings-form"
            onSubmit={(event) => {
              event.preventDefault();
              create.mutate();
            }}
          >
            <label>
              <span>Display name</span>
              <input
                value={form.name}
                onChange={(event) => setForm({ ...form, name: event.target.value })}
                required
                placeholder="Main lobby TV"
              />
            </label>
            <div className="notice">
              <ShieldCheck size={18} />
              <span>
                The private numeric player identifier is generated automatically. It does not need
                to be entered on the TV.
              </span>
            </div>
            <div className="form-grid">
              <label>
                <span>Timezone</span>
                <TimezoneSelect
                  value={form.timezone}
                  onChange={(timezone) => setForm({ ...form, timezone })}
                />
              </label>
              <label>
                <span>Orientation</span>
                <select
                  value={form.orientation}
                  onChange={(event) => setForm({ ...form, orientation: event.target.value })}
                >
                  <option value="landscape">Landscape</option>
                  <option value="portrait">Portrait</option>
                </select>
              </label>
              {zoomSettings("create-tv")}
            </div>
            <fieldset className="tv-resolution-settings">
              <legend>Configured canvas</legend>
              <p className="field-help">
                Sets the target dimensions shown in configuration previews. The live player still
                adapts to the browser or TV viewport it actually reports.
              </p>
              <div className="form-grid">
                <label>
                  <span>Width (px)</span>
                  <input
                    type="number"
                    min="320"
                    max="20000"
                    step="1"
                    value={form.resolutionWidth}
                    onChange={(event) =>
                      setForm({ ...form, resolutionWidth: event.target.valueAsNumber })
                    }
                    required
                  />
                </label>
                <label>
                  <span>Height (px)</span>
                  <input
                    type="number"
                    min="240"
                    max="20000"
                    step="1"
                    value={form.resolutionHeight}
                    onChange={(event) =>
                      setForm({ ...form, resolutionHeight: event.target.valueAsNumber })
                    }
                    required
                  />
                </label>
              </div>
            </fieldset>
            <label>
              <span>Active playlist</span>
              <select
                value={form.activePlaylistId}
                onChange={(event) => setForm({ ...form, activePlaylistId: event.target.value })}
              >
                <option value="">No playlist assigned</option>
                {playlistsQuery.data?.map((playlist) => (
                  <option value={playlist.id} key={playlist.id}>
                    {playlist.name}
                  </option>
                ))}
              </select>
            </label>
            {backgroundSettings()}
            {audioSettings()}
            <ErrorNotice error={create.error} />
            <div className="modal-actions">
              <button
                type="button"
                className="button button--secondary"
                onClick={() => setCreating(false)}
              >
                Cancel
              </button>
              <button type="submit" className="button button--primary" disabled={create.isPending}>
                Create & generate code
              </button>
            </div>
          </form>
        </Modal>
      ) : null}
      {activation ? (
        <Modal
          title="Activate the television"
          description="Enter this rotating code on the player. It remains visible on the television card and is replaced after use or expiry."
          onClose={() => setActivation(null)}
        >
          <div
            className="activation-code"
            aria-label={`Activation code ${activation.code.split("").join(" ")}`}
          >
            {activation.code}
          </div>
          <div className="notice">
            <MonitorPlay size={18} />
            <span>
              Open{" "}
              <a href={publicPlayerUrl} target="_blank" rel="noreferrer">
                {publicPlayerUrl}
              </a>{" "}
              on the display and enter this code. The code links this browser to the correct
              customer and TV automatically.
            </span>
          </div>
          <div className="modal-actions">
            <button
              className="button button--primary button--wide"
              onClick={() => setActivation(null)}
            >
              Done
            </button>
          </div>
        </Modal>
      ) : null}
      {editingTv ? (
        <Modal
          title={`Edit ${editingTv.name}`}
          description="Update the display identity, screen settings, content playlist, and autoplaying MP3 audio."
          onClose={() => setEditingTv(null)}
          size="large"
        >
          <form
            className="form-stack tv-settings-form"
            onSubmit={(event) => {
              event.preventDefault();
              update.mutate();
            }}
          >
            <label>
              <span>Display name</span>
              <input
                value={form.name}
                onChange={(event) => setForm({ ...form, name: event.target.value })}
                required
              />
            </label>
            <div className="form-grid">
              <label>
                <span>Timezone</span>
                <TimezoneSelect
                  value={form.timezone}
                  onChange={(timezone) => setForm({ ...form, timezone })}
                  required
                />
              </label>
              <label>
                <span>Orientation</span>
                <select
                  value={form.orientation}
                  onChange={(event) => setForm({ ...form, orientation: event.target.value })}
                >
                  <option value="landscape">Landscape</option>
                  <option value="portrait">Portrait</option>
                </select>
              </label>
              {zoomSettings("edit-tv")}
            </div>
            <fieldset className="tv-resolution-settings">
              <legend>Configured canvas</legend>
              <p className="field-help">
                Sets the target dimensions shown in configuration previews. The live player still
                adapts to the browser or TV viewport it actually reports.
              </p>
              <div className="form-grid">
                <label>
                  <span>Width (px)</span>
                  <input
                    type="number"
                    min="320"
                    max="20000"
                    step="1"
                    value={form.resolutionWidth}
                    onChange={(event) =>
                      setForm({ ...form, resolutionWidth: event.target.valueAsNumber })
                    }
                    required
                  />
                </label>
                <label>
                  <span>Height (px)</span>
                  <input
                    type="number"
                    min="240"
                    max="20000"
                    step="1"
                    value={form.resolutionHeight}
                    onChange={(event) =>
                      setForm({ ...form, resolutionHeight: event.target.valueAsNumber })
                    }
                    required
                  />
                </label>
              </div>
            </fieldset>
            <label>
              <span>Active playlist</span>
              <select
                value={form.activePlaylistId}
                onChange={(event) => setForm({ ...form, activePlaylistId: event.target.value })}
              >
                <option value="">No playlist assigned</option>
                {playlistsQuery.data?.map((playlist) => (
                  <option value={playlist.id} key={playlist.id}>
                    {playlist.name}
                  </option>
                ))}
              </select>
            </label>
            {backgroundSettings()}
            {audioSettings()}
            <ErrorNotice error={update.error} />
            <div className="modal-actions">
              <button
                type="button"
                className="button button--secondary"
                onClick={() => setEditingTv(null)}
              >
                Cancel
              </button>
              <button type="submit" className="button button--primary" disabled={update.isPending}>
                {update.isPending ? "Saving…" : "Save television"}
              </button>
            </div>
          </form>
        </Modal>
      ) : null}
    </div>
  );
}

interface AssetRow {
  id: string;
  clientId: string | null;
  name: string;
  publicUrl: string;
  mimeType: string;
  sizeBytes: number;
  createdAt: string;
  uploadedBy: {
    type: "platform" | "client";
    name: string;
    email: string;
  } | null;
}

interface AssetUploadLimits {
  imageMb: number;
  videoMb: number | null;
  audioMb: number;
  allowedMimeTypes: string[];
}

interface AssetUploadQueueItem {
  id: string;
  name: string;
  progress: number;
  status: "queued" | "uploading" | "complete" | "failed";
  error?: string;
}

function AudioLibraryUploadStatus({
  queue,
  uploading,
  onDismiss,
}: {
  queue: AssetUploadQueueItem[];
  uploading: boolean;
  onDismiss: () => void;
}) {
  if (!queue.length) return null;
  const completed = queue.filter((item) => item.status === "complete").length;
  const failed = queue.filter((item) => item.status === "failed").length;
  const overallProgress = Math.round(
    queue.reduce(
      (total, item) =>
        total + (item.status === "complete" || item.status === "failed" ? 100 : item.progress),
      0,
    ) / queue.length,
  );
  return (
    <section
      className="asset-upload-status audio-library-upload-status"
      aria-label="MP3 upload status"
      aria-live="polite"
    >
      <div className="asset-upload-status__summary">
        <div>
          <strong>
            {uploading ? `Uploading ${queue.length} MP3 files` : "MP3 upload finished"}
          </strong>
          <span>
            {completed} of {queue.length} files uploaded
            {failed ? ` · ${failed} failed` : ""}
          </span>
        </div>
        {!uploading ? (
          <button type="button" className="text-button" onClick={onDismiss}>
            <X size={15} /> Dismiss status
          </button>
        ) : null}
      </div>
      <progress aria-label="Overall MP3 upload progress" value={overallProgress} max={100} />
      <ul className="asset-upload-list">
        {queue.map((item) => (
          <li key={item.id}>
            <span title={item.name}>{item.name}</span>
            <span>
              {item.status === "failed"
                ? item.error
                : item.status === "complete"
                  ? "Complete"
                  : item.status === "queued"
                    ? "Queued"
                    : item.progress >= 100
                      ? "Processing…"
                      : `${item.progress}%`}
            </span>
          </li>
        ))}
      </ul>
    </section>
  );
}

export function AdminAudioLibrariesPage() {
  const queryClient = useQueryClient();
  const [editingAudioLibrary, setEditingAudioLibrary] = useState<AudioLibraryRow | null>(null);
  const [creatingAudioLibrary, setCreatingAudioLibrary] = useState(false);
  const [activeAudioLibraryId, setActiveAudioLibraryId] = useState("");
  const [libraryAssetToAdd, setLibraryAssetToAdd] = useState("");
  const [uploadingLibraryTracks, setUploadingLibraryTracks] = useState(false);
  const [libraryUploadQueue, setLibraryUploadQueue] = useState<AssetUploadQueueItem[]>([]);
  const [libraryUploadError, setLibraryUploadError] = useState<unknown>(null);
  const [draggedLibraryAssetId, setDraggedLibraryAssetId] = useState("");
  const [audioLibraryForm, setAudioLibraryForm] = useState({
    name: "",
    audioAssetIds: [] as string[],
    controllerEnabled: false,
  });
  const assetsQuery = useQuery({
    queryKey: ["admin", "assets"],
    queryFn: () => api<AssetRow[]>("/api/v1/admin/assets"),
    retry: false,
  });
  const audioUploadLimitsQuery = useQuery({
    queryKey: ["admin", "assets", "upload-limits"],
    queryFn: () => api<AssetUploadLimits>("/api/v1/admin/assets/upload-limits"),
    retry: false,
  });
  const audioLibrariesQuery = useQuery({
    queryKey: ["admin", "audio-libraries"],
    queryFn: () => api<AudioLibraryRow[]>("/api/v1/admin/audio-libraries"),
  });
  const saveAudioLibrary = useMutation({
    mutationFn: () =>
      api<{ id: string }>(
        editingAudioLibrary
          ? `/api/v1/admin/audio-libraries/${editingAudioLibrary.id}`
          : "/api/v1/admin/audio-libraries",
        {
          method: editingAudioLibrary ? "PATCH" : "POST",
          body: JSON.stringify(audioLibraryForm),
        },
      ),
    onSuccess: async (saved) => {
      setActiveAudioLibraryId(saved.id);
      await queryClient.invalidateQueries({ queryKey: ["admin", "audio-libraries"] });
      setCreatingAudioLibrary(false);
      setEditingAudioLibrary(null);
      setLibraryAssetToAdd("");
    },
  });
  const removeAudioLibrary = useMutation({
    mutationFn: (id: string) => api(`/api/v1/admin/audio-libraries/${id}`, { method: "DELETE" }),
    onSuccess: async (_, id) => {
      if (activeAudioLibraryId === id) setActiveAudioLibraryId("");
      await queryClient.invalidateQueries({ queryKey: ["admin", "audio-libraries"] });
    },
  });
  const updateAudioLibraryTracks = useMutation({
    mutationFn: ({
      library,
      audioAssetIds,
    }: {
      library: AudioLibraryRow;
      audioAssetIds: string[];
    }) =>
      api(`/api/v1/admin/audio-libraries/${library.id}`, {
        method: "PATCH",
        body: JSON.stringify({
          name: library.name,
          audioAssetIds,
          controllerEnabled: library.controllerEnabled,
        }),
      }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["admin", "audio-libraries"] }),
  });
  const platformMp3Assets =
    assetsQuery.data?.filter(
      (asset) => asset.clientId === null && asset.mimeType === "audio/mpeg",
    ) ?? [];
  const availableLibraryAssets = platformMp3Assets.filter(
    (asset) => !audioLibraryForm.audioAssetIds.includes(asset.id),
  );
  const activeAudioLibrary =
    audioLibrariesQuery.data?.find((library) => library.id === activeAudioLibraryId) ??
    audioLibrariesQuery.data?.[0] ??
    null;
  useEffect(() => {
    if (!audioLibrariesQuery.data?.length) return;
    if (!activeAudioLibraryId) setActiveAudioLibraryId(audioLibrariesQuery.data[0]!.id);
  }, [activeAudioLibraryId, audioLibrariesQuery.data]);
  function startAudioLibrary(library?: AudioLibraryRow): void {
    setEditingAudioLibrary(library ?? null);
    setCreatingAudioLibrary(!library);
    setLibraryAssetToAdd("");
    setLibraryUploadQueue([]);
    setLibraryUploadError(null);
    setAudioLibraryForm({
      name: library?.name ?? "",
      audioAssetIds: library?.items.map((item) => item.assetId) ?? [],
      controllerEnabled: library?.controllerEnabled ?? false,
    });
  }
  function closeAudioLibrary(): void {
    setCreatingAudioLibrary(false);
    setEditingAudioLibrary(null);
    setLibraryAssetToAdd("");
  }
  function moveLibraryTrack(assetId: string, direction: -1 | 1): void {
    setAudioLibraryForm((current) => {
      const index = current.audioAssetIds.indexOf(assetId);
      const target = index + direction;
      if (index < 0 || target < 0 || target >= current.audioAssetIds.length) return current;
      const next = [...current.audioAssetIds];
      [next[index], next[target]] = [next[target]!, next[index]!];
      return { ...current, audioAssetIds: next };
    });
  }
  async function uploadLibraryFiles(files: File[], library?: AudioLibraryRow): Promise<void> {
    if (!files.length || uploadingLibraryTracks) return;
    setUploadingLibraryTracks(true);
    setLibraryUploadError(null);
    const queuedFiles = files.map((file) => ({
      file,
      item: {
        id: crypto.randomUUID(),
        name: file.name,
        progress: 0,
        status: "queued" as const,
      },
    }));
    setLibraryUploadQueue(queuedFiles.map(({ item }) => item));
    const uploaded: AssetRow[] = [];
    const failures: string[] = [];
    try {
      for (const { file, item } of queuedFiles) {
        const maximumAudioBytes = audioUploadLimitsQuery.data?.audioMb
          ? audioUploadLimitsQuery.data.audioMb * 1024 * 1024
          : null;
        const validationError =
          !/\.mp3$/i.test(file.name) && file.type !== "audio/mpeg"
            ? "Choose an MP3 audio file."
            : maximumAudioBytes !== null && file.size > maximumAudioBytes
              ? `${file.name} is ${(file.size / 1024 / 1024).toFixed(1)} MB; the configured MP3 limit is ${audioUploadLimitsQuery.data!.audioMb} MB.`
              : null;
        if (validationError) {
          failures.push(`${file.name}: ${validationError}`);
          setLibraryUploadQueue((current) =>
            current.map((queued) =>
              queued.id === item.id
                ? { ...queued, status: "failed", error: validationError }
                : queued,
            ),
          );
          continue;
        }
        setLibraryUploadQueue((current) =>
          current.map((queued) =>
            queued.id === item.id ? { ...queued, status: "uploading" } : queued,
          ),
        );
        try {
          uploaded.push(
            await uploadAssetWithProgress<AssetRow>(file, "admin", (progress) =>
              setLibraryUploadQueue((current) =>
                current.map((queued) => (queued.id === item.id ? { ...queued, progress } : queued)),
              ),
            ),
          );
          setLibraryUploadQueue((current) =>
            current.map((queued) =>
              queued.id === item.id ? { ...queued, progress: 100, status: "complete" } : queued,
            ),
          );
        } catch (error) {
          const message = error instanceof Error ? error.message : "The upload failed.";
          failures.push(`${file.name}: ${message}`);
          setLibraryUploadQueue((current) =>
            current.map((queued) =>
              queued.id === item.id ? { ...queued, status: "failed", error: message } : queued,
            ),
          );
        }
      }
      await queryClient.invalidateQueries({ queryKey: ["admin", "assets"] });
      if (library && uploaded.length) {
        const nextIds = [
          ...library.items.map((item) => item.assetId),
          ...uploaded.map(({ id }) => id),
        ];
        await api(`/api/v1/admin/audio-libraries/${library.id}`, {
          method: "PATCH",
          body: JSON.stringify({
            name: library.name,
            audioAssetIds: nextIds,
            controllerEnabled: library.controllerEnabled,
          }),
        });
        await queryClient.invalidateQueries({ queryKey: ["admin", "audio-libraries"] });
      } else if (uploaded.length) {
        setAudioLibraryForm((current) => ({
          ...current,
          audioAssetIds: [...current.audioAssetIds, ...uploaded.map(({ id }) => id)],
        }));
      }
      if (failures.length) setLibraryUploadError(new Error(failures.join(" ")));
    } catch (error) {
      setLibraryUploadError(error);
    } finally {
      setUploadingLibraryTracks(false);
    }
  }
  function reorderActiveLibrary(sourceId: string, targetId: string, placement: "before" | "after") {
    if (!activeAudioLibrary || sourceId === targetId || updateAudioLibraryTracks.isPending) return;
    const ids = activeAudioLibrary.items.map((item) => item.assetId);
    const sourceIndex = ids.indexOf(sourceId);
    const targetIndex = ids.indexOf(targetId);
    if (sourceIndex < 0 || targetIndex < 0) return;
    const next = [...ids];
    next.splice(sourceIndex, 1);
    const adjustedTarget = next.indexOf(targetId);
    next.splice(placement === "after" ? adjustedTarget + 1 : adjustedTarget, 0, sourceId);
    updateAudioLibraryTracks.mutate({ library: activeAudioLibrary, audioAssetIds: next });
  }
  function moveActiveLibraryTrack(assetId: string, direction: -1 | 1): void {
    if (!activeAudioLibrary) return;
    const ids = activeAudioLibrary.items.map((item) => item.assetId);
    const index = ids.indexOf(assetId);
    const target = index + direction;
    if (index < 0 || target < 0 || target >= ids.length) return;
    const next = [...ids];
    [next[index], next[target]] = [next[target]!, next[index]!];
    updateAudioLibraryTracks.mutate({ library: activeAudioLibrary, audioAssetIds: next });
  }
  return (
    <div className="page-stack">
      <section className="page-actions">
        <div>
          <h2>Predefined audio lists</h2>
          <p>
            Build reusable ordered playlists from platform MP3 assets for clients to assign to TVs.
          </p>
        </div>
        <button
          type="button"
          className="button button--primary"
          onClick={() => startAudioLibrary()}
          disabled={assetsQuery.isLoading}
        >
          <Plus size={16} /> New audio list
        </button>
      </section>
      <ErrorNotice
        error={
          assetsQuery.error ??
          audioUploadLimitsQuery.error ??
          audioLibrariesQuery.error ??
          removeAudioLibrary.error ??
          updateAudioLibraryTracks.error ??
          libraryUploadError
        }
      />
      {assetsQuery.isLoading || audioLibrariesQuery.isLoading ? (
        <PageLoading />
      ) : audioLibrariesQuery.data?.length ? (
        <>
          <section className="card content-playlist-bar">
            <label className="content-playlist-picker">
              <span>Active audio list</span>
              <select
                aria-label="Active audio list"
                value={activeAudioLibrary?.id ?? ""}
                onChange={(event) => setActiveAudioLibraryId(event.target.value)}
              >
                {audioLibrariesQuery.data.map((library) => (
                  <option value={library.id} key={library.id}>
                    {library.name} · {library.items.length} track
                    {library.items.length === 1 ? "" : "s"}
                  </option>
                ))}
              </select>
            </label>
            <div className="content-playlist-summary">
              <span className="status-pill status-pill--active">Available</span>
              <span>
                {activeAudioLibrary?.items.length ?? 0} track
                {activeAudioLibrary?.items.length === 1 ? "" : "s"}
              </span>
            </div>
            <div className="content-playlist-actions">
              <button
                type="button"
                className={`button ${activeAudioLibrary?.controllerEnabled ? "button--primary" : "button--secondary"}`}
                disabled={!activeAudioLibrary || updateAudioLibraryTracks.isPending}
                onClick={() => {
                  if (!activeAudioLibrary) return;
                  void api(`/api/v1/admin/audio-libraries/${activeAudioLibrary.id}`, {
                    method: "PATCH",
                    body: JSON.stringify({
                      name: activeAudioLibrary.name,
                      audioAssetIds: activeAudioLibrary.items.map((item) => item.assetId),
                      controllerEnabled: !activeAudioLibrary.controllerEnabled,
                    }),
                  }).then(() =>
                    queryClient.invalidateQueries({ queryKey: ["admin", "audio-libraries"] }),
                  );
                }}
              >
                <Gamepad2 size={15} />{" "}
                {activeAudioLibrary?.controllerEnabled
                  ? "Added To Controller"
                  : "Add To Controller"}
              </button>
              <button
                type="button"
                className="button button--secondary"
                disabled={!activeAudioLibrary}
                onClick={() => activeAudioLibrary && startAudioLibrary(activeAudioLibrary)}
              >
                <Pencil size={15} /> Rename or edit
              </button>
              <button
                type="button"
                className="button button--danger"
                disabled={!activeAudioLibrary || removeAudioLibrary.isPending}
                onClick={() => {
                  if (activeAudioLibrary && window.confirm(`Delete ${activeAudioLibrary.name}?`))
                    removeAudioLibrary.mutate(activeAudioLibrary.id);
                }}
              >
                <Trash2 size={15} /> Delete audio list
              </button>
            </div>
          </section>
          {activeAudioLibrary ? (
            <section className="playlist-slides-panel">
              <div className="page-actions">
                <div>
                  <h2>Tracks in {activeAudioLibrary.name}</h2>
                  <p>Upload MP3 files directly, then drag tracks to set their playback order.</p>
                </div>
                <label
                  className={`button button--primary ${uploadingLibraryTracks ? "is-disabled" : ""}`}
                >
                  <CloudUpload size={16} />
                  {uploadingLibraryTracks
                    ? `Uploading ${libraryUploadQueue.length} MP3 files…`
                    : "Upload multiple MP3 tracks"}
                  <input
                    className="visually-hidden"
                    type="file"
                    aria-label="Upload multiple MP3 tracks"
                    accept=".mp3,audio/mpeg"
                    multiple
                    disabled={uploadingLibraryTracks}
                    onChange={(event) => {
                      void uploadLibraryFiles(
                        Array.from(event.target.files ?? []),
                        activeAudioLibrary,
                      );
                      event.target.value = "";
                    }}
                  />
                </label>
              </div>
              <AudioLibraryUploadStatus
                queue={libraryUploadQueue}
                uploading={uploadingLibraryTracks}
                onDismiss={() => setLibraryUploadQueue([])}
              />
              {activeAudioLibrary.items.length ? (
                <ol
                  className="tv-audio-list audio-library-track-list"
                  aria-label="Audio list order"
                >
                  {activeAudioLibrary.items.map((item, index) => (
                    <li
                      key={item.assetId}
                      className={draggedLibraryAssetId === item.assetId ? "is-dragging" : ""}
                      onDragOver={(event) => {
                        if (!draggedLibraryAssetId || draggedLibraryAssetId === item.assetId)
                          return;
                        event.preventDefault();
                        event.dataTransfer.dropEffect = "move";
                      }}
                      onDrop={(event) => {
                        event.preventDefault();
                        const sourceId =
                          event.dataTransfer.getData("text/plain") || draggedLibraryAssetId;
                        const bounds = event.currentTarget.getBoundingClientRect();
                        reorderActiveLibrary(
                          sourceId,
                          item.assetId,
                          event.clientY < bounds.top + bounds.height / 2 ? "before" : "after",
                        );
                        setDraggedLibraryAssetId("");
                      }}
                    >
                      <span className="tv-audio-position">{index + 1}</span>
                      <span
                        className="drag-handle"
                        draggable
                        tabIndex={0}
                        role="slider"
                        aria-label={`Reorder ${item.assetName}. Use drag and drop or the arrow keys.`}
                        aria-valuemin={1}
                        aria-valuemax={activeAudioLibrary.items.length}
                        aria-valuenow={index + 1}
                        onDragStart={(event) => {
                          setDraggedLibraryAssetId(item.assetId);
                          event.dataTransfer.effectAllowed = "move";
                          event.dataTransfer.setData("text/plain", item.assetId);
                        }}
                        onDragEnd={() => setDraggedLibraryAssetId("")}
                        onKeyDown={(event) => {
                          if (event.key !== "ArrowUp" && event.key !== "ArrowDown") return;
                          event.preventDefault();
                          moveActiveLibraryTrack(item.assetId, event.key === "ArrowUp" ? -1 : 1);
                        }}
                      >
                        <GripVertical size={17} /> Drag to reorder
                      </span>
                      <span className="tv-audio-name">
                        <Music2 size={16} />
                        <strong>{item.assetName}</strong>
                      </span>
                      <button
                        type="button"
                        className="text-button text-button--danger"
                        disabled={
                          activeAudioLibrary.items.length === 1 ||
                          updateAudioLibraryTracks.isPending
                        }
                        title={
                          activeAudioLibrary.items.length === 1
                            ? "An audio list must keep at least one track."
                            : undefined
                        }
                        onClick={() =>
                          updateAudioLibraryTracks.mutate({
                            library: activeAudioLibrary,
                            audioAssetIds: activeAudioLibrary.items
                              .filter((candidate) => candidate.assetId !== item.assetId)
                              .map((candidate) => candidate.assetId),
                          })
                        }
                      >
                        <Trash2 size={15} /> Remove
                      </button>
                    </li>
                  ))}
                </ol>
              ) : null}
            </section>
          ) : null}
        </>
      ) : (
        <section className="empty-state card">
          <span className="empty-icon">
            <Music2 />
          </span>
          <h2>No predefined audio lists yet</h2>
          <p>Create a reusable ordered list and upload multiple MP3 tracks directly into it.</p>
          <button
            type="button"
            className="button button--primary"
            onClick={() => startAudioLibrary()}
          >
            Create audio list
          </button>
        </section>
      )}
      {creatingAudioLibrary || editingAudioLibrary ? (
        <Modal
          title={editingAudioLibrary ? `Edit ${editingAudioLibrary.name}` : "New audio list"}
          description="Choose platform MP3 tracks and set the order used by client TVs."
          onClose={closeAudioLibrary}
          size="large"
        >
          <form
            className="form-stack"
            onSubmit={(event) => {
              event.preventDefault();
              saveAudioLibrary.mutate();
            }}
          >
            <label>
              <span>List name</span>
              <input
                value={audioLibraryForm.name}
                maxLength={120}
                required
                onChange={(event) =>
                  setAudioLibraryForm({ ...audioLibraryForm, name: event.target.value })
                }
              />
            </label>
            <button
              type="button"
              className={`button ${audioLibraryForm.controllerEnabled ? "button--primary" : "button--secondary"}`}
              onClick={() =>
                setAudioLibraryForm({
                  ...audioLibraryForm,
                  controllerEnabled: !audioLibraryForm.controllerEnabled,
                })
              }
            >
              <Gamepad2 size={16} />{" "}
              {audioLibraryForm.controllerEnabled ? "Added To Controller" : "Add To Controller"}
            </button>
            <label className="audio-library-direct-upload">
              <span>Upload MP3 files directly to this list</span>
              <small>
                Select multiple files
                {audioUploadLimitsQuery.data
                  ? ` · maximum ${audioUploadLimitsQuery.data.audioMb} MB per MP3`
                  : " · loading configured limit…"}
              </small>
              <span className="button button--secondary">
                <CloudUpload size={16} />
                {uploadingLibraryTracks
                  ? `Uploading ${libraryUploadQueue.length} MP3 files…`
                  : "Choose multiple MP3 files"}
              </span>
              <input
                className="visually-hidden"
                type="file"
                aria-label="Choose multiple MP3 files"
                accept=".mp3,audio/mpeg"
                multiple
                disabled={uploadingLibraryTracks}
                onChange={(event) => {
                  void uploadLibraryFiles(Array.from(event.target.files ?? []));
                  event.target.value = "";
                }}
              />
            </label>
            <AudioLibraryUploadStatus
              queue={libraryUploadQueue}
              uploading={uploadingLibraryTracks}
              onDismiss={() => setLibraryUploadQueue([])}
            />
            <div className="tv-audio-picker">
              <label>
                <span>Add platform MP3</span>
                <select
                  value={libraryAssetToAdd}
                  onChange={(event) => setLibraryAssetToAdd(event.target.value)}
                  disabled={!availableLibraryAssets.length}
                >
                  <option value="">
                    {availableLibraryAssets.length ? "Choose an MP3" : "All MP3 tracks added"}
                  </option>
                  {availableLibraryAssets.map((asset) => (
                    <option value={asset.id} key={asset.id}>
                      {asset.name}
                    </option>
                  ))}
                </select>
              </label>
              <button
                type="button"
                className="button button--secondary"
                disabled={!libraryAssetToAdd}
                onClick={() => {
                  setAudioLibraryForm((current) => ({
                    ...current,
                    audioAssetIds: [...current.audioAssetIds, libraryAssetToAdd],
                  }));
                  setLibraryAssetToAdd("");
                }}
              >
                <Plus size={16} /> Add track
              </button>
            </div>
            <ol className="tv-audio-list" aria-label="Predefined audio list order">
              {audioLibraryForm.audioAssetIds.map((assetId, index) => {
                const asset = platformMp3Assets.find((candidate) => candidate.id === assetId);
                return (
                  <li key={assetId}>
                    <span className="tv-audio-position">{index + 1}</span>
                    <span className="tv-audio-name">
                      <Music2 size={16} />
                      <strong>{asset?.name ?? "Unavailable MP3 asset"}</strong>
                    </span>
                    <div className="card-inline-actions">
                      <button
                        type="button"
                        className="icon-button"
                        aria-label={`Move ${asset?.name ?? "track"} up`}
                        disabled={index === 0}
                        onClick={() => moveLibraryTrack(assetId, -1)}
                      >
                        <ArrowUp size={15} />
                      </button>
                      <button
                        type="button"
                        className="icon-button"
                        aria-label={`Move ${asset?.name ?? "track"} down`}
                        disabled={index === audioLibraryForm.audioAssetIds.length - 1}
                        onClick={() => moveLibraryTrack(assetId, 1)}
                      >
                        <ArrowDown size={15} />
                      </button>
                      <button
                        type="button"
                        className="text-button text-button--danger"
                        onClick={() =>
                          setAudioLibraryForm((current) => ({
                            ...current,
                            audioAssetIds: current.audioAssetIds.filter((id) => id !== assetId),
                          }))
                        }
                      >
                        <Trash2 size={15} /> Remove
                      </button>
                    </div>
                  </li>
                );
              })}
            </ol>
            <ErrorNotice error={saveAudioLibrary.error ?? libraryUploadError} />
            <div className="modal-actions">
              <button
                type="button"
                className="button button--secondary"
                onClick={closeAudioLibrary}
              >
                Cancel
              </button>
              <button
                type="submit"
                className="button button--primary"
                disabled={
                  !audioLibraryForm.name.trim() ||
                  !audioLibraryForm.audioAssetIds.length ||
                  saveAudioLibrary.isPending
                }
              >
                {saveAudioLibrary.isPending ? "Saving…" : "Save audio list"}
              </button>
            </div>
          </form>
        </Modal>
      ) : null}
    </div>
  );
}

export function AssetsPage({ mode = "client" }: { mode?: AccessMode }) {
  const queryClient = useQueryClient();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [uploadError, setUploadError] = useState<unknown>(null);
  const [uploadQueue, setUploadQueue] = useState<AssetUploadQueueItem[]>([]);
  const [search, setSearch] = useState("");
  const [typeFilter, setTypeFilter] = useState("all");
  const [uploaderFilter, setUploaderFilter] = useState("all");
  const [selectedAssetIds, setSelectedAssetIds] = useState<Set<string>>(new Set());
  const [previewAsset, setPreviewAsset] = useState<AssetRow | null>(null);
  const [bulkRemoving, setBulkRemoving] = useState(false);
  const [bulkRemoveError, setBulkRemoveError] = useState<unknown>(null);
  const query = useQuery({
    queryKey: [mode, "assets"],
    queryFn: () =>
      api<AssetRow[]>(mode === "admin" ? "/api/v1/admin/assets" : "/api/v1/client/assets"),
    retry: false,
  });
  const limitsQuery = useQuery({
    queryKey: [mode, "assets", "upload-limits"],
    queryFn: () =>
      api<AssetUploadLimits>(
        mode === "admin"
          ? "/api/v1/admin/assets/upload-limits"
          : "/api/v1/client/assets/upload-limits",
      ),
    retry: false,
  });
  const remove = useMutation({
    mutationFn: (id: string) =>
      api(mode === "admin" ? `/api/v1/admin/assets/${id}` : `/api/v1/client/assets/${id}`, {
        method: "DELETE",
      }),
    onSuccess: async (_, id) => {
      setSelectedAssetIds((current) => {
        const next = new Set(current);
        next.delete(id);
        return next;
      });
      await queryClient.invalidateQueries({ queryKey: [mode, "assets"] });
    },
  });

  const uploading = uploadQueue.some(
    (item) => item.status === "queued" || item.status === "uploading",
  );
  const assetType = (asset: AssetRow): "image" | "video" | "audio" =>
    asset.mimeType.startsWith("image/")
      ? "image"
      : asset.mimeType.startsWith("video/")
        ? "video"
        : "audio";
  const filteredAssets = (query.data ?? []).filter((asset) => {
    const normalizedSearch = search.trim().toLocaleLowerCase();
    const matchesSearch =
      !normalizedSearch ||
      [asset.name, asset.mimeType, asset.uploadedBy?.name, asset.uploadedBy?.email].some((value) =>
        value?.toLocaleLowerCase().includes(normalizedSearch),
      );
    const matchesType = typeFilter === "all" || assetType(asset) === typeFilter;
    const uploaderKey = asset.uploadedBy
      ? `${asset.uploadedBy.type}:${asset.uploadedBy.email}`
      : "unknown";
    return (
      matchesSearch && matchesType && (uploaderFilter === "all" || uploaderKey === uploaderFilter)
    );
  });
  const uploaderOptions = [
    ...new Map(
      (query.data ?? []).map((asset) => {
        const key = asset.uploadedBy
          ? `${asset.uploadedBy.type}:${asset.uploadedBy.email}`
          : "unknown";
        return [
          key,
          {
            key,
            label: asset.uploadedBy
              ? `${asset.uploadedBy.name} (${asset.uploadedBy.email})`
              : "Unknown uploader",
          },
        ] as const;
      }),
    ).values(),
  ];
  const allVisibleSelected =
    filteredAssets.length > 0 && filteredAssets.every((asset) => selectedAssetIds.has(asset.id));
  const overallUploadProgress = uploadQueue.length
    ? Math.round(
        uploadQueue.reduce(
          (total, item) =>
            total + (item.status === "complete" || item.status === "failed" ? 100 : item.progress),
          0,
        ) / uploadQueue.length,
      )
    : 0;

  function resolvedFileType(file: File): string {
    if (/\.mp3$/i.test(file.name)) return "audio/mpeg";
    return file.type;
  }

  function fileLimitError(file: File): string | null {
    const limits = limitsQuery.data;
    if (!limits) return null;
    const mimeType = resolvedFileType(file);
    if (!limits.allowedMimeTypes.includes(mimeType))
      return `${file.name} is not a supported asset type.`;
    const maximumMb = mimeType.startsWith("video/")
      ? limits.videoMb
      : mimeType.startsWith("audio/")
        ? limits.audioMb
        : limits.imageMb;
    if (maximumMb === null || file.size <= maximumMb * 1024 * 1024) return null;
    return `${file.name} is ${(file.size / 1024 / 1024).toFixed(1)} MB; the configured ${assetType({
      mimeType,
    } as AssetRow)} limit is ${maximumMb} MB.`;
  }

  async function selectFiles(files: File[]): Promise<void> {
    if (!files.length || uploading) return;
    setUploadError(null);
    const queuedFiles = files.map((file) => ({
      file,
      item: {
        id: crypto.randomUUID(),
        name: file.name,
        progress: 0,
        status: "queued" as const,
      },
    }));
    setUploadQueue(queuedFiles.map(({ item }) => item));
    for (const { file, item } of queuedFiles) {
      const validationError = fileLimitError(file);
      if (validationError) {
        setUploadQueue((current) =>
          current.map((queued) =>
            queued.id === item.id
              ? { ...queued, status: "failed", error: validationError }
              : queued,
          ),
        );
        continue;
      }
      setUploadQueue((current) =>
        current.map((queued) =>
          queued.id === item.id ? { ...queued, status: "uploading" } : queued,
        ),
      );
      try {
        await uploadAssetWithProgress<AssetRow>(file, mode, (progress) =>
          setUploadQueue((current) =>
            current.map((queued) => (queued.id === item.id ? { ...queued, progress } : queued)),
          ),
        );
        setUploadQueue((current) =>
          current.map((queued) =>
            queued.id === item.id ? { ...queued, progress: 100, status: "complete" } : queued,
          ),
        );
      } catch (error) {
        const message = error instanceof Error ? error.message : "The upload failed.";
        setUploadQueue((current) =>
          current.map((queued) =>
            queued.id === item.id ? { ...queued, status: "failed", error: message } : queued,
          ),
        );
        setUploadError(error);
      }
    }
    await queryClient.invalidateQueries({ queryKey: [mode, "assets"] });
  }

  function toggleAsset(id: string): void {
    setSelectedAssetIds((current) => {
      const next = new Set(current);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }

  async function removeSelectedAssets(): Promise<void> {
    if (!selectedAssetIds.size) return;
    if (!window.confirm(`Permanently remove ${selectedAssetIds.size} selected assets?`)) return;
    setBulkRemoving(true);
    setBulkRemoveError(null);
    const failures: string[] = [];
    for (const id of selectedAssetIds) {
      try {
        await api(mode === "admin" ? `/api/v1/admin/assets/${id}` : `/api/v1/client/assets/${id}`, {
          method: "DELETE",
        });
      } catch (error) {
        failures.push(error instanceof Error ? error.message : "An asset could not be removed.");
      }
    }
    await queryClient.invalidateQueries({ queryKey: [mode, "assets"] });
    if (failures.length) setBulkRemoveError(new Error(failures.join(" ")));
    else setSelectedAssetIds(new Set());
    setBulkRemoving(false);
  }

  return (
    <div className="page-stack">
      <section className="page-actions">
        <div>
          <h2>Asset library</h2>
          <p>
            {mode === "admin"
              ? "Upload platform-owned branding images and MP3 tracks."
              : "Immutable, content-hashed images, videos, and MP3 audio ready for player caching."}
          </p>
        </div>
        <label className="button button--primary file-button">
          <CloudUpload size={17} />{" "}
          {uploading ? `Uploading ${uploadQueue.length} files…` : "Upload assets"}
          <input
            ref={fileInputRef}
            type="file"
            aria-label={mode === "admin" ? "Upload platform asset" : "Upload client asset"}
            multiple
            accept={
              mode === "admin"
                ? "image/jpeg,image/png,image/webp,audio/mpeg,.mp3"
                : "image/jpeg,image/png,image/webp,video/mp4,video/webm,audio/mpeg,.mp3"
            }
            disabled={uploading}
            onChange={(event) => {
              void selectFiles(Array.from(event.target.files ?? []));
              event.currentTarget.value = "";
            }}
          />
        </label>
      </section>
      <section className="asset-upload-limits card" aria-label="Configured upload limits">
        <strong>Configured upload limits</strong>
        {limitsQuery.data ? (
          <span>
            Images {limitsQuery.data.imageMb} MB · MP3 {limitsQuery.data.audioMb} MB
            {limitsQuery.data.videoMb === null ? "" : ` · Video ${limitsQuery.data.videoMb} MB`}
          </span>
        ) : (
          <span>Loading limits…</span>
        )}
      </section>
      {uploadQueue.length ? (
        <section className="asset-upload-status card" aria-label="Asset upload status">
          <div className="asset-upload-status__summary">
            <div>
              <strong>{uploading ? "Uploading assets" : "Upload finished"}</strong>
              <span>
                {uploadQueue.filter((item) => item.status === "complete").length} of{" "}
                {uploadQueue.length} complete
              </span>
            </div>
            {!uploading ? (
              <button type="button" className="text-button" onClick={() => setUploadQueue([])}>
                <X size={15} /> Dismiss status
              </button>
            ) : null}
          </div>
          <progress aria-label="Overall upload progress" value={overallUploadProgress} max={100} />
          <ul className="asset-upload-list">
            {uploadQueue.map((item) => (
              <li key={item.id}>
                <span title={item.name}>{item.name}</span>
                <span>
                  {item.status === "failed"
                    ? item.error
                    : item.status === "complete"
                      ? "Complete"
                      : `${item.progress}%`}
                </span>
              </li>
            ))}
          </ul>
        </section>
      ) : null}
      <ErrorNotice error={uploadError} />
      <ErrorNotice error={remove.error} />
      <ErrorNotice error={bulkRemoveError} />
      <section className="asset-filters card" aria-label="Asset filters">
        <label className="search-field">
          <Search size={16} />
          <span className="sr-only">Search assets</span>
          <input
            type="search"
            value={search}
            placeholder="Search name, type, or uploader"
            onChange={(event) => setSearch(event.target.value)}
          />
        </label>
        <label>
          <span>Asset type</span>
          <select value={typeFilter} onChange={(event) => setTypeFilter(event.target.value)}>
            <option value="all">All types</option>
            <option value="image">Images</option>
            <option value="video">Videos</option>
            <option value="audio">Audio</option>
          </select>
        </label>
        <label>
          <span>Uploaded by</span>
          <select
            value={uploaderFilter}
            onChange={(event) => setUploaderFilter(event.target.value)}
          >
            <option value="all">All uploaders</option>
            {uploaderOptions.map((uploader) => (
              <option key={uploader.key} value={uploader.key}>
                {uploader.label}
              </option>
            ))}
          </select>
        </label>
        <button
          type="button"
          className="button button--secondary"
          onClick={() => {
            setSearch("");
            setTypeFilter("all");
            setUploaderFilter("all");
          }}
        >
          Clear filters
        </button>
      </section>
      {query.data?.length ? (
        <section className="asset-selection-bar card" aria-label="Asset selection">
          <label>
            <input
              type="checkbox"
              checked={allVisibleSelected}
              onChange={() =>
                setSelectedAssetIds((current) => {
                  const next = new Set(current);
                  for (const asset of filteredAssets) {
                    if (allVisibleSelected) next.delete(asset.id);
                    else next.add(asset.id);
                  }
                  return next;
                })
              }
            />
            Select all visible
          </label>
          <span>
            {filteredAssets.length} shown · {selectedAssetIds.size} selected
          </span>
          {selectedAssetIds.size ? (
            <div>
              <button
                type="button"
                className="text-button"
                onClick={() => setSelectedAssetIds(new Set())}
              >
                <X size={15} /> Clear selection
              </button>
              <button
                type="button"
                className="text-button text-button--danger"
                disabled={bulkRemoving}
                onClick={() => void removeSelectedAssets()}
              >
                <Trash2 size={15} />{" "}
                {bulkRemoving ? "Removing…" : `Remove selected (${selectedAssetIds.size})`}
              </button>
            </div>
          ) : null}
        </section>
      ) : null}
      {query.isLoading ? (
        <PageLoading />
      ) : query.error ? (
        <ErrorNotice error={query.error} />
      ) : filteredAssets.length ? (
        <div className="asset-grid">
          {filteredAssets.map((asset) => (
            <article
              className={`asset-card ${
                selectedAssetIds.has(asset.id) ? "asset-card--selected" : ""
              }`}
              key={asset.id}
            >
              <label className="asset-select">
                <input
                  type="checkbox"
                  checked={selectedAssetIds.has(asset.id)}
                  onChange={() => toggleAsset(asset.id)}
                />
                <span>Select {asset.name}</span>
              </label>
              <button
                type="button"
                className="asset-preview"
                aria-label={`Preview ${asset.name}`}
                onClick={() => setPreviewAsset(asset)}
              >
                {asset.mimeType.startsWith("image/") ? (
                  <img src={asset.publicUrl} alt="" loading="lazy" />
                ) : asset.mimeType === "audio/mpeg" ? (
                  <Music2 aria-label="MP3 audio" />
                ) : (
                  <MonitorPlay />
                )}
              </button>
              <div className="asset-copy">
                <strong>{asset.name}</strong>
                <span>
                  {asset.mimeType} · {(asset.sizeBytes / 1024 / 1024).toFixed(1)} MB
                </span>
                <span>
                  Uploaded by{" "}
                  {asset.uploadedBy
                    ? `${asset.uploadedBy.name} (${asset.uploadedBy.email})`
                    : "Unknown"}
                </span>
              </div>
              <button
                type="button"
                className="text-button text-button--danger asset-remove"
                disabled={remove.isPending}
                onClick={() => {
                  if (window.confirm(`Permanently remove ${asset.name}?`)) remove.mutate(asset.id);
                }}
              >
                <Trash2 size={15} /> Remove asset
              </button>
            </article>
          ))}
        </div>
      ) : (
        <section className="empty-state card">
          <span className="empty-icon">
            <FileImage />
          </span>
          <h2>No assets yet</h2>
          <p>
            {query.data?.length
              ? "No assets match the current filters."
              : mode === "admin"
                ? "Upload one or more JPEG, PNG, WebP, or MP3 platform assets. Signatures and configured size limits are validated by the API."
                : "Upload one or more JPEG, PNG, WebP, MP4, WebM, or MP3 files. Signatures and configured size limits are validated by the API."}
          </p>
        </section>
      )}
      {previewAsset ? (
        <Modal
          title={previewAsset.name}
          description={`${previewAsset.mimeType} · ${(previewAsset.sizeBytes / 1024 / 1024).toFixed(
            1,
          )} MB`}
          onClose={() => setPreviewAsset(null)}
          size="large"
        >
          <div className="asset-modal-preview">
            {previewAsset.mimeType.startsWith("image/") ? (
              <img src={previewAsset.publicUrl} alt={previewAsset.name} />
            ) : previewAsset.mimeType.startsWith("video/") ? (
              <video src={previewAsset.publicUrl} controls preload="metadata">
                <track kind="captions" />
              </video>
            ) : (
              <audio src={previewAsset.publicUrl} controls preload="metadata" />
            )}
          </div>
          <dl className="asset-modal-details">
            <div>
              <dt>Uploaded by</dt>
              <dd>
                {previewAsset.uploadedBy
                  ? `${previewAsset.uploadedBy.name} (${previewAsset.uploadedBy.email})`
                  : "Unknown"}
              </dd>
            </div>
            <div>
              <dt>Uploaded</dt>
              <dd>{new Date(previewAsset.createdAt).toLocaleString()}</dd>
            </div>
          </dl>
          <div className="modal-actions">
            <button
              type="button"
              className="button button--secondary"
              onClick={() => setPreviewAsset(null)}
            >
              Close preview
            </button>
          </div>
        </Modal>
      ) : null}
    </div>
  );
}
